import React, { useEffect, useState, useCallback, useRef, useContext, useMemo } from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Box,
  CircularProgress,
  Alert,
  TableHead,
  Button,
  Autocomplete,
  TextField,
  Typography,
  Checkbox,
  IconButton,
} from '@mui/material';
import UndoIcon from '@mui/icons-material/Undo';
import LogoutIcon from '@mui/icons-material/Logout';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { format, parse, isValid, subWeeks, isSaturday } from 'date-fns';
import { sheetsService } from '../services/googleSheetsService';
import { backendService } from '../services/backendService';
import { CreateSheetDialog } from './CreateSheetDialog';
import { UrlDialog } from './UrlDialog';
import { useNavigate } from 'react-router-dom';
import { ChartUploadDialog } from './ChartUploadDialog';
import { AddNewVideoDialog } from '../components/AddNewVideoDialog';
import { processChartText } from '../services/chartProcessingService';
import { AddVideoDetailsDialog } from '../components/AddVideoDetailsDialog';
import { YouTubeSearchDialog } from './YouTubeSearchDialog';
import { youtubeService } from '../services/youtubeService';
import { PickersDay, PickersDayProps, DateView } from '@mui/x-date-pickers';
import { normalizeSongArtistKey, normalizeSongTitle } from '../utils/stringNormalization';
import { ReorderDialog } from './ReorderDialog';
import { AuthContext } from '../contexts/AuthContext'; // Import AuthContext instead of useAuth
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import { DriveFile } from '../types/google-api'; // Import DriveFile type
import { useSnackbar, SnackbarKey } from 'notistack'; // Added for notifications and SnackbarKey

// Add interface for change history
interface ChangeHistory {
  row: number;
  col: number;
  oldValue: string;
  newValue: string;
  range: string;
}

interface Video {
  song: string;
  artist: string;
  youtubeLink?: string;
}

interface SpreadsheetViewerProps {
  spreadsheetId: string;  // This is actually the folder ID
  selectedDate: Date | null;
  onDateChange: (date: Date | null) => void;
  setChartDate?: React.Dispatch<React.SetStateAction<string>>;
}

interface VideoManagerData {
  song?: string;
  artist?: string;
  youtubeLink?: string;
  voiceOverText?: string;
}

// Add data type interface between other interfaces
interface SpreadsheetData {
  headers: string[];
  rows: string[][];
  originalHeaders?: string[];
  originalRows?: string[][];
}

// Add helper function to extract YouTube video ID
const getYouTubeVideoId = (url: string): string | null => {
  if (!url) return null;
  
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\n?#]+)/,
    /youtube\.com\/embed\/([^&\n?#]+)/,
    /youtube\.com\/v\/([^&\n?#]+)/
  ];

  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match && match[1]) {
      return match[1];
    }
  }
  return null;
};

// ---> START: Add shared helper function
// Helper function to convert 0-based index to column letter (A=0, B=1, ... Z=25, AA=26)
const columnIndexToLetter = (index: number): string => {
  let result = '';
  while (index >= 0) {
    result = String.fromCharCode(65 + (index % 26)) + result;
    index = Math.floor(index / 26) - 1;
  }
  return result;
};
// ---> END: Add shared helper function

// ---> START: Add Render Folder ID Constant
const RENDERS_FOLDER_ID = '1WYxCqZA9GntggbYfDC3cUe6PTGqwgQQR';
// ---> END: Add Render Folder ID Constant

// Update the RenderDialog component with proper typing
const RenderDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  spreadsheetId: string | null; // Keep spreadsheetId
  voUrlColumnIndex: number | null; // Add the original column index
  voInPoints: { [key: string]: string };
  voOutPoints: { [key: string]: string };
}> = ({ open, onClose, spreadsheetId, voUrlColumnIndex, voInPoints, voOutPoints }): React.ReactElement => { // <-- Add return type here
  // State to hold the fetched URL
  const [recentVoUrl, setRecentVoUrl] = useState<string>('');
  const [loadingUrl, setLoadingUrl] = useState<boolean>(false);
  const [fetchError, setFetchError] = useState<string | null>(null);

  useEffect(() => {
    const fetchVoUrl = async () => {
      // Only fetch if dialog is open, we have ID, and a valid column index
      if (!open || !spreadsheetId || voUrlColumnIndex === null || voUrlColumnIndex < 0) {
        setRecentVoUrl('');
        setFetchError(null);
        setLoadingUrl(false); // Ensure loading is stopped
        if (open && spreadsheetId && voUrlColumnIndex === null) {
           console.warn('[RenderDialog] Cannot fetch VO URL: voUrlColumnIndex is null.');
           // Optionally set an error message here
        }
        return;
      }

      setLoadingUrl(true);
      setFetchError(null);
      setRecentVoUrl('');

      try {
        const columnLetter = columnIndexToLetter(voUrlColumnIndex);
        // Fetch from the specific cell in row 14
        const range = `Sheet1!${columnLetter}14`;
        console.log(`[RenderDialog] Fetching VO URL from ${spreadsheetId}, range ${range} (Index: ${voUrlColumnIndex})`);
        const result = await sheetsService.getData(spreadsheetId, range);

        if (result.values && result.values.length > 0 && result.values[0].length > 0) {
          const url = String(result.values[0][0]).trim();
          console.log(`[RenderDialog] Fetched VO URL: "${url}"`);
          setRecentVoUrl(url);
        } else {
          console.log(`[RenderDialog] No VO URL found at ${range}`);
          setRecentVoUrl('');
        }
      } catch (error) {
        console.error('[RenderDialog] Error fetching VO URL:', error);
        setFetchError('Failed to fetch VO URL.');
        setRecentVoUrl('');
      } finally {
        setLoadingUrl(false);
      }
    };

    fetchVoUrl();
  }, [open, spreadsheetId, voUrlColumnIndex]); // Add voUrlColumnIndex dependency


  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>VO Render Preview</DialogTitle>
      <DialogContent>
        <Typography variant="subtitle1" gutterBottom>
          Most Recent VO File: {loadingUrl ? <CircularProgress size={16} /> : fetchError || recentVoUrl || 'None'}
        </Typography>
        {fetchError && <Alert severity="error">{fetchError}</Alert>}
        <List>
          {['Intro', '10', '9', '8', '7', '6', '5', '4', '3', '2', '1', 'Outro'].map((key) => (
            <ListItem key={key}>
              <ListItemText
                primary={key}
                secondary={`In: ${voInPoints[key] || '-'} | Out: ${voOutPoints[key] || '-'}`}
              />
            </ListItem>
          ))}
        </List>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export const SpreadsheetViewer: React.FC<SpreadsheetViewerProps> = ({
  spreadsheetId: folderId,
  selectedDate,
  onDateChange,
  setChartDate,
}) => {
  const navigate = useNavigate();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const [data, setData] = useState<SpreadsheetData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedSpreadsheet, setSelectedSpreadsheet] = useState<string | null>(null);
  const [isSignedIn, setIsSignedIn] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [availableDates, setAvailableDates] = useState<Date[]>([]);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editingUrl, setEditingUrl] = useState<{ row: number; col: number; url: string } | null>(null);
  const [editingS3Url, setEditingS3Url] = useState<{ row: number; col: number; url: string } | null>(null);
  const [uploadChartDialogOpen, setUploadChartDialogOpen] = useState(false);
  const [searchDialogOpen, setSearchDialogOpen] = useState(false);
  const [searchTargetRowIndex, setSearchTargetRowIndex] = useState<number | null>(null);
  const [searchInitialQuery, setSearchInitialQuery] = useState('');
  const [videoData, setVideoData] = useState<{ [key: string]: VideoManagerData }>({});
  const [changeHistory, setChangeHistory] = useState<ChangeHistory[]>([]);
  const [isExportingSocial, setIsExportingSocial] = useState(false);
  const [sortAscending, setSortAscending] = useState(true);
  const [songArtistSuggestions, setSongArtistSuggestions] = useState<string[]>([]);
  const [artistSuggestions, setArtistSuggestions] = useState<string[]>([]);
  const [reorderDialogOpen, setReorderDialogOpen] = useState(false);
  const [addNewVideoDialogOpen, setAddNewVideoDialogOpen] = useState(false);
  const [newVideosToAdd, setNewVideosToAdd] = useState<{song: string, artist: string}[]>([]);
  const [uploadedChartText, setUploadedChartText] = useState<string>('');
  const [addVideoDialogOpen, setAddVideoDialogOpen] = useState(false);
  const [voInPoints, setVoInPoints] = useState<{ [key: string]: string }>({});
  const [voOutPoints, setVoOutPoints] = useState<{ [key: string]: string }>({});
  const [voUrlColumnIndex, setVoUrlColumnIndex] = useState<number | null>(null);
  const [voSongColIndex, setVoSongColIndex] = useState<number | null>(null);
  const [voInPointColIndex, setVoInPointColIndex] = useState<number | null>(null);
  const [voOutPointColIndex, setVoOutPointColIndex] = useState<number | null>(null);
  const [voPositionColIndex, setVoPositionColIndex] = useState<number | null>(null);
  const [socialExportJobId, setSocialExportJobId] = useState<string | null>(null);
  const [pollIntervalId, setPollIntervalId] = useState<NodeJS.Timeout | null>(null);
  const [progressSnackbarKey, setProgressSnackbarKey] = useState<SnackbarKey | null>(null);
  const [lastSnackbarMessage, setLastSnackbarMessage] = useState<string | null>(null);
  const [lastMessageTimestamp, setLastMessageTimestamp] = useState<number>(0);
  const VIDEO_MANAGER_SPREADSHEET_ID = '1aVv8xC1HIB1XQDDwF8wiLtkB9c-D_g4zPnhFhbizkTo';
  const [editingCell, setEditingCell] = useState<{ row: number; col: number; value: string } | null>(null);
  
  // Add handler to toggle sort direction
  const toggleSortDirection = () => {
    setSortAscending(prev => !prev);
  };

  // Cleanup polling interval on component unmount or if job finishes/errors
  useEffect(() => {
    return () => {
      if (pollIntervalId) {
        clearInterval(pollIntervalId);
        setPollIntervalId(null);
      }
      if (progressSnackbarKey) {
        closeSnackbar(progressSnackbarKey);
        setProgressSnackbarKey(null);
      }
    };
  }, [pollIntervalId, progressSnackbarKey, closeSnackbar]);

  // Add cleanup for social export job
  useEffect(() => {
    return () => {
      if (socialExportJobId) {
        setSocialExportJobId(null);
      }
    };
  }, [socialExportJobId]);

  // First, memoize the getFormattedDate function
  const getFormattedDate = useCallback((date: Date): string => {
    return format(date, 'M-d-yy');
  }, []);

  // Function to update the weekly sheet with vertical URLs from job results
  const updateWeeklySheetWithVerticalUrls = useCallback(async (weeklySheetId: string, results: any[]) => {
    if (!weeklySheetId || !results || results.length === 0) {
      console.warn('[updateWeeklySheetWithVerticalUrls] No sheet ID or results to update');
      return;
    }

    console.log(`[updateWeeklySheetWithVerticalUrls] Updating weekly sheet ${weeklySheetId} with ${results.length} results`);
    
    // Filter successful results with vertical URLs
    const successResults = results.filter(r => r.success && r.verticalUrl);
    if (successResults.length === 0) {
      console.warn('[updateWeeklySheetWithVerticalUrls] No successful results with vertical URLs to update');
      return;
    }
    
    // Get sheet data to find vertical URL column
    const sheetData = await sheetsService.getData(weeklySheetId, 'Sheet1!A1:Z1');
    if (!sheetData.values || sheetData.values.length === 0) {
      throw new Error('Could not find header row in weekly sheet');
    }
    
    const headers = sheetData.values[0];
    const verticalUrlColIndex = headers.findIndex((h: string) => 
      h.toString().toLowerCase() === 'vertical url'
    );
    
    if (verticalUrlColIndex === -1) {
      throw new Error('Could not find "Vertical URL" column in weekly sheet');
    }
    
    // Get position column to match with results
    const positionColIndex = headers.findIndex((h: string) => 
      h.toString().toLowerCase() === 'position'
    );
    
    if (positionColIndex === -1) {
      throw new Error('Could not find "Position" column in weekly sheet');
    }
    
    // Get all data to find rows to update
    const allSheetData = await sheetsService.getData(weeklySheetId, 'Sheet1!A1:Z1000');
    if (!allSheetData.values || allSheetData.values.length <= 1) {
      throw new Error('Could not find data rows in weekly sheet');
    }
    
    const dataRows = allSheetData.values.slice(1); // Skip header row
    
    // Prepare batch updates
    const batchUpdates = [];
    
    // For each successful result, find the matching row and update vertical URL
    for (const result of successResults) {
      const position = result.position;
      const verticalUrl = result.verticalUrl;
      
      // Find row index for this position
      const rowIndex = dataRows.findIndex(row => {
        const rowPosition = String(row[positionColIndex] || '').trim();
        return rowPosition === position;
      });
      
      if (rowIndex === -1) {
        console.warn(`[updateWeeklySheetWithVerticalUrls] Could not find row for position ${position}`);
        continue;
      }
      
      // Row index in sheet is +2 (header row + 0-indexing)
      const sheetRowIndex = rowIndex + 2;
      const columnLetter = columnIndexToLetter(verticalUrlColIndex);
      const range = `Sheet1!${columnLetter}${sheetRowIndex}`;
      
      console.log(`[updateWeeklySheetWithVerticalUrls] Will update position ${position} at ${range} with URL ${verticalUrl}`);
      
      batchUpdates.push({
        range,
        values: [[verticalUrl]]
      });
    }
    
    if (batchUpdates.length === 0) {
      console.warn('[updateWeeklySheetWithVerticalUrls] No updates to make');
      return;
    }
    
    // Execute batch update
    console.log(`[updateWeeklySheetWithVerticalUrls] Executing batch update with ${batchUpdates.length} updates`);
    // Use batchUpdateValues instead of batchUpdate
    await sheetsService.batchUpdateValues(weeklySheetId, batchUpdates);
    console.log('[updateWeeklySheetWithVerticalUrls] Weekly sheet updated successfully');
  }, [sheetsService]);

  // --- NEW Polling Function ---
  const pollJobStatus = useCallback(async (jobId: string, key: SnackbarKey) => {
    console.log(`[PollStatus] Checking job ${jobId}`);
    try {
              const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/social-clip-status/${jobId}`);
      if (!response.ok) {
        // Handle non-2xx responses (e.g., 404 Not Found)
        const errorData = await response.json().catch(() => ({ error: 'Failed to parse error response' }));
        console.error(`[PollStatus] Error fetching status for job ${jobId}: ${response.status}`, errorData);
        if (key) { // Check if key exists before updating
          enqueueSnackbar(`Error polling job status: ${errorData.error || response.statusText}`, { // Replaced updateSnackbar
              variant: 'error',
              // key: key, // No need to reuse key for a final error message
              persist: false,
          });
        }
        if (pollIntervalId) clearInterval(pollIntervalId);
        setPollIntervalId(null);
        setIsExportingSocial(false); // Re-enable button on error
        setSocialExportJobId(null);
        setProgressSnackbarKey(null); // Clear the key once the job is done
        setLastSnackbarMessage(null); // Reset last message on error
        setLastMessageTimestamp(0); // Reset timestamp
        return;
      }

      const jobStatus = await response.json();
      console.log(`[PollStatus] Job ${jobId} status:`, jobStatus);

      const currentMessage = jobStatus.message || 'Processing...';
      console.log(`[PollStatus] Current message: "${currentMessage}", Previous message: "${lastSnackbarMessage}"`);
      
      // Filter out ANY message containing "polling" anywhere in the text - no exceptions
      const isPollingMessage = currentMessage.toLowerCase().includes('polling');
      console.log(`[PollStatus] Is polling message: ${isPollingMessage}`);

      // Get current time to check against minimum display duration
      const currentTime = Date.now();
      const timeElapsedSinceLastMessage = currentTime - lastMessageTimestamp;
      const isCompletionOrError = jobStatus.status === 'completed' || jobStatus.status === 'failed';
      const shouldUpdateMessage = 
        (timeElapsedSinceLastMessage >= 5000) || // 5 seconds have passed
        (lastSnackbarMessage === null) || // First message
        isCompletionOrError; // Completion or error messages should show immediately
      
      console.log(`[PollStatus] Time since last message: ${timeElapsedSinceLastMessage}ms, Should update: ${shouldUpdateMessage}`);

      // Update snackbar message if key exists AND message changed AND it's NOT a polling update
      // AND minimum display time has elapsed or it's a completion/error message
      if (key && currentMessage !== lastSnackbarMessage && !isPollingMessage && shouldUpdateMessage) {
          console.log(`[PollStatus] DISPLAYING NEW MESSAGE: "${currentMessage}"`);
          enqueueSnackbar(currentMessage, { 
              key: key, 
              persist: true,
              autoHideDuration: null // Ensure it doesn't auto-hide
          }); 
          setLastSnackbarMessage(currentMessage); // Update the last shown message
          setLastMessageTimestamp(currentTime); // Update the timestamp
      } else {
          console.log(`[PollStatus] NOT displaying message because: ` + 
                    `${!key ? 'key missing' : ''}` +
                    `${currentMessage === lastSnackbarMessage ? ', message unchanged' : ''}` +
                    `${isPollingMessage ? ', is polling message' : ''}` +
                    `${!shouldUpdateMessage ? ', minimum display time not reached' : ''}`);
      }

      // Check if job is finished or failed
      if (jobStatus.status === 'completed' || jobStatus.status === 'failed') {
        if (pollIntervalId) clearInterval(pollIntervalId);
        setPollIntervalId(null);
        setIsExportingSocial(false); // Re-enable button
        setSocialExportJobId(null);
        setProgressSnackbarKey(null); // Clear the key once the job is done
        setLastSnackbarMessage(null); // Reset last message on job end
        setLastMessageTimestamp(0); // Reset timestamp

        // Final notification update if key exists
        if (key) {
          closeSnackbar(key); // Close the persistent snackbar
          const finalVariant = jobStatus.status === 'completed' ? 'success' : 'error';
          let finalMessage = jobStatus.message || 'Job finished.';
          if (jobStatus.status === 'completed' && jobStatus.results) {
               const successCount = jobStatus.results.filter((r: any) => r.success).length;
               finalMessage = `Job completed: ${successCount}/${jobStatus.totalClips} clips generated successfully.`;
               
               // If completed successfully, update the weekly sheet with vertical URLs
               if (successCount > 0 && selectedDate) {
                 try {
                   // Update the spreadsheet with vertical URLs from results
                   await updateWeeklySheetWithVerticalUrls(jobStatus.weeklySheetId, jobStatus.results);
                   
                   // Refresh the data to show the updated vertical URLs
                   if (data && selectedSpreadsheet) {
                     const sheetName = format(selectedDate, 'M-d-yy');
                     console.log(`[PollStatus] Reloading sheet data for ${sheetName} after updating vertical URLs`);
                     
                     try {
                       // Use a standard sheet name that shouldn't cause problems
                       const result = await sheetsService.getData(selectedSpreadsheet, `Sheet1!A1:Z1000`);
                       
                       if (result.values && result.values.length > 0) {
                         const headers = result.values[0];
                         const rows = result.values.slice(1);
                         const newData: SpreadsheetData = {
                           headers,
                           rows,
                           originalHeaders: [...headers],
                           originalRows: rows.map(row => [...row])
                         };
                         setData(newData);
                         console.log(`[PollStatus] Sheet data reloaded successfully`);
                       }
                     } catch (reloadError: any) {
                       // Just log the reload error to console but don't show a snackbar
                       // since the vertical URLs were already added successfully
                       console.warn(`[PollStatus] Non-critical error reloading data: ${reloadError.message}`);
                       console.log('[PollStatus] This error is expected with hyphenated sheet names and doesn\'t affect functionality');
                     }
                   }
                 } catch (updateError: any) {
                   // Only show error for actual update failures, not for reload issues
                   console.error('[PollStatus] Error updating weekly sheet with vertical URLs:', updateError);
                   enqueueSnackbar(`Error updating spreadsheet with vertical URLs: ${updateError.message}`, { 
                     variant: 'error',
                     persist: false
                   });
                 }
               }
          } else if (jobStatus.status === 'failed') {
               finalMessage = `Job failed: ${jobStatus.error || 'Unknown error'}`;
          }

          enqueueSnackbar(finalMessage, { // Replaced updateSnackbar
            variant: finalVariant,
            // No need to pass key here for the final message, let notistack manage it, or pass a NEW key if specific control is needed
            persist: false, // Allow it to auto-hide now
            autoHideDuration: 6000, // Show for 6 seconds
          });
        } // end if(key)
      }
      // If still processing, the interval will call this function again
    } catch (error) {
      console.error(`[PollStatus] Network or parsing error polling job ${jobId}:`, error);
      if (progressSnackbarKey) { // Use progressSnackbarKey directly here as 'key' is not in scope for this catch block
           closeSnackbar(progressSnackbarKey); // Close existing persistent snackbar
           enqueueSnackbar('Network error while polling job status.', { // Replaced updateSnackbar
               variant: 'error',
               // key: progressSnackbarKey, // No need to reuse key for a final error message
               persist: false,
           });
      }
      if (pollIntervalId) clearInterval(pollIntervalId);
      setPollIntervalId(null);
      setIsExportingSocial(false);
      setSocialExportJobId(null);
      setProgressSnackbarKey(null);
      setLastSnackbarMessage(null); // Reset last message on error
      setLastMessageTimestamp(0); // Reset timestamp
    }
  // Include all dependencies needed for this function
  }, [pollIntervalId, enqueueSnackbar, progressSnackbarKey, lastSnackbarMessage, lastMessageTimestamp, selectedDate, data, selectedSpreadsheet, sheetsService, updateWeeklySheetWithVerticalUrls, setData]);

  // Memoize the formatChartDate function
  const formatChartDate = useCallback((date: Date): string => {
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                      'July', 'August', 'September', 'October', 'November', 'December'];
    return `${monthNames[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
  }, []);

  const CustomDay = useCallback((props: any) => {
    const { day, ...other } = props;
    const isDaySaturday = isSaturday(day);
    const formattedDay = getFormattedDate(day);
    const isAvailable = availableDates.some(availableDate => 
      getFormattedDate(availableDate) === formattedDay
    );

    return (
      <PickersDay
        {...other}
        day={day}
        sx={{
          color: isDaySaturday ? (isAvailable ? 'black' : 'gray') : 'gray',
          '&.Mui-selected': {
            backgroundColor: 'black',
            color: 'white',
            '&:hover': {
              backgroundColor: 'black'
            }
          },
          '&:hover': {
            backgroundColor: isDaySaturday ? 'rgba(0, 0, 0, 0.04)' : 'transparent'
          }
        }}
      >
        {isDaySaturday && !isAvailable ? (
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              fontSize: '1.2rem',
              fontWeight: 'bold',
              color: 'gray'
            }}
          >
            +
          </Box>
        ) : (
          day.getDate()
        )}
      </PickersDay>
    );
  }, [availableDates, getFormattedDate]);

  // First, define the syncVideoDataFromMaster function before it's referenced
  // This needs to be at the correct scope level - before loadData and where it won't cause circular dependencies
  const syncVideoDataFromMaster = useCallback(async (weeklySpreadsheetId: string, currentData: SpreadsheetData | null): Promise<void> => {
    if (!weeklySpreadsheetId) return;
    
    console.log(`[syncVideoDataFromMaster] Starting sync for spreadsheet: ${weeklySpreadsheetId}`);
    
    try {
      // Constants for spreadsheet IDs
      const MASTER_SHEET_ID = '1aVv8xC1HIB1XQDDwF8wiLtkB9c-D_g4zPnhFhbizkTo';
      
      // Skip sync if we're trying to update the master sheet itself
      if (weeklySpreadsheetId === MASTER_SHEET_ID) {
        console.log('[syncVideoDataFromMaster] Skipping sync because current sheet IS the master sheet');
        return;
      }
      
      // Use existing data when available
      let weeklyHeaders: string[] = [];
      let weeklyRows: string[][] = [];
      
      // Use the provided data or fetch it if not available
      if (currentData?.originalHeaders && currentData?.originalRows) {
        console.log('[syncVideoDataFromMaster] Using provided data for weekly sheet');
        weeklyHeaders = currentData.originalHeaders;
        weeklyRows = currentData.originalRows;
      } else {
        // Fallback if data wasn't provided
        console.log('[syncVideoDataFromMaster] No data provided, fetching from weekly sheet');
        const weeklyData = await sheetsService.getData(weeklySpreadsheetId, 'Sheet1!A:Z');
        if (!weeklyData.values || weeklyData.values.length === 0) {
          console.warn('[syncVideoDataFromMaster] No data found in weekly sheet');
          return;
        }
        weeklyHeaders = weeklyData.values[0].map(String);
        weeklyRows = weeklyData.values.slice(1).map(row => row.map(String));
      }
      
      // Get data from master sheet (single API call)
      console.log('[syncVideoDataFromMaster] Fetching data from master sheet');
      const masterData = await sheetsService.getData(MASTER_SHEET_ID, 'Sheet1!A:Z');
      
      // Validate we have data from master sheet
      if (!masterData.values || masterData.values.length === 0) {
        console.warn('[syncVideoDataFromMaster] No data found in master video sheet');
        return;
      }

      // Find columns we need in the weekly sheet
      const songColIndex = weeklyHeaders.findIndex(h => 
        h?.toString().toLowerCase().includes('song') || 
        h?.toString().toLowerCase().includes('title'));
      const artistColIndex = weeklyHeaders.findIndex(h => 
        h?.toString().toLowerCase().includes('artist'));
      const s3ColIndex = weeklyHeaders.findIndex(h => 
        h?.toString().toLowerCase().includes('s3'));
      const inPointColIndex = weeklyHeaders.findIndex(h => 
        h?.toString().toLowerCase().includes('in point'));
      const seekInColIndex = weeklyHeaders.findIndex(h => 
        h?.toString().toLowerCase().includes('seek in'));
      const durationColIndex = weeklyHeaders.findIndex(h => 
        h?.toString().toLowerCase().includes('duration'));
      const zoomColIndex = weeklyHeaders.findIndex(h => 
        h?.toString().toLowerCase() === 'zoom');
      // Add weekly Vertical URL column lookup
      const verticalUrlColIndex = weeklyHeaders.findIndex(h => 
        h?.toString().toLowerCase() === 'vertical url');
      
      if (songColIndex === -1 || artistColIndex === -1) {
        console.warn('[syncVideoDataFromMaster] Could not find song or artist columns in weekly sheet');
        return;
      }
      
      // Check if we have columns to update
      const hasColumnsToUpdate = s3ColIndex !== -1 || inPointColIndex !== -1 || seekInColIndex !== -1 || durationColIndex !== -1 || zoomColIndex !== -1 || verticalUrlColIndex !== -1;
      if (!hasColumnsToUpdate) {
        console.warn('[syncVideoDataFromMaster] No video data columns found to update in weekly sheet');
        return;
      }

      // Find columns we need in the master sheet
      const masterHeaders = masterData.values[0];
      const masterSongColIndex = masterHeaders.findIndex(h => 
        h?.toString().toLowerCase().includes('song') || 
        h?.toString().toLowerCase().includes('title'));
      const masterArtistColIndex = masterHeaders.findIndex(h => 
        h?.toString().toLowerCase().includes('artist'));
      const masterS3ColIndex = masterHeaders.findIndex(h => 
        h?.toString().toLowerCase().includes('s3'));
      const masterInPointColIndex = masterHeaders.findIndex(h => 
        h?.toString().toLowerCase().includes('in point'));
      const masterSeekInColIndex = masterHeaders.findIndex(h => 
        h?.toString().toLowerCase().includes('seek in'));
      const masterDurationColIndex = masterHeaders.findIndex(h => 
        h?.toString().toLowerCase().includes('duration'));
      // Add master Zoom column lookup
      const masterZoomColIndex = masterHeaders.findIndex(h => 
        h?.toString().toLowerCase() === 'zoom');
      // Add master Vertical URL column lookup
      const masterVerticalUrlColIndex = masterHeaders.findIndex(h => 
        h?.toString().toLowerCase() === 'vertical url');
      
      if (masterSongColIndex === -1 || masterArtistColIndex === -1) {
        console.warn('[syncVideoDataFromMaster] Could not find song or artist columns in master sheet');
        return;
      }

      // Build a lookup map from the master sheet for faster matching
      console.log('[syncVideoDataFromMaster] Building lookup map from master sheet');
      const masterLookupMap = new Map();
      
      // Process each row in the master sheet
      masterData.values.slice(1).forEach((row: any[]) => {
        const song = String(row[masterSongColIndex] ?? '').trim();
        const artist = String(row[masterArtistColIndex] ?? '').trim();
        const s3Url = masterS3ColIndex !== -1 ? String(row[masterS3ColIndex] ?? '').trim() : '';
        const inPoint = masterInPointColIndex !== -1 ? String(row[masterInPointColIndex] ?? '0').trim() : '0';
        const seekIn = masterSeekInColIndex !== -1 ? String(row[masterSeekInColIndex] ?? '0').trim() : '0';
        const duration = masterDurationColIndex !== -1 ? String(row[masterDurationColIndex] ?? '0').trim() : '0';
        const zoom = masterZoomColIndex !== -1 ? String(row[masterZoomColIndex] ?? '').trim() : '';
        // Get the Vertical URL if it exists
        const verticalUrl = masterVerticalUrlColIndex !== -1 ? String(row[masterVerticalUrlColIndex] ?? '').trim() : '';
        
        if (s3Url) {
          const key = normalizeSongArtistKey(song, artist);
          masterLookupMap.set(key, {
            s3Url,
            inPoint,
            seekIn,
            duration,
            zoom,
            verticalUrl
          });
        }
      });
      
      console.log(`[syncVideoDataFromMaster] Built lookup map with ${masterLookupMap.size} entries from master sheet`);

      // Prepare batch updates - collect ALL needed updates before making API calls
      const batchUpdates = [];
      let updateCount = 0;
      let matchCount = 0;

      // For each song in the weekly sheet (skip header row)
      for (let weeklyIdx = 0; weeklyIdx < weeklyRows.length; weeklyIdx++) {
        const weeklyRow = weeklyRows[weeklyIdx];
        if (!weeklyRow[songColIndex] || !weeklyRow[artistColIndex]) continue;
        
        const weeklySong = weeklyRow[songColIndex]?.toString().trim();
        const weeklyArtist = weeklyRow[artistColIndex]?.toString().trim();
        if (!weeklySong || !weeklySong.length) continue;
        
        // Normalize song/artist for consistent matching
        const weeklyKey = normalizeSongArtistKey(weeklySong, weeklyArtist);
        
        // Look for a match in our master lookup map
        const masterInfo = masterLookupMap.get(weeklyKey);
        if (masterInfo) {
          matchCount++;
          let rowUpdated = false;
          
          // Update S3 link if available
          if (s3ColIndex !== -1 && masterInfo.s3Url) {
            const range = `Sheet1!${columnIndexToLetter(s3ColIndex)}${weeklyIdx + 2}`;
            batchUpdates.push({
              range,
              values: [[masterInfo.s3Url]]
            });
            updateCount++;
            rowUpdated = true;
          }
          
          // Update In Point if available
          if (inPointColIndex !== -1 && masterInfo.inPoint) {
            const range = `Sheet1!${columnIndexToLetter(inPointColIndex)}${weeklyIdx + 2}`;
            batchUpdates.push({
              range,
              values: [[masterInfo.inPoint]]
            });
            updateCount++;
            rowUpdated = true;
          }
          
          // Update Seek In if available
          if (seekInColIndex !== -1 && masterInfo.seekIn) {
            const range = `Sheet1!${columnIndexToLetter(seekInColIndex)}${weeklyIdx + 2}`;
            batchUpdates.push({
              range,
              values: [[masterInfo.seekIn]]
            });
            updateCount++;
            rowUpdated = true;
          }
          
          // Update Duration if available
          if (durationColIndex !== -1 && masterInfo.duration) {
            const range = `Sheet1!${columnIndexToLetter(durationColIndex)}${weeklyIdx + 2}`;
            batchUpdates.push({
              range,
              values: [[masterInfo.duration]]
            });
            updateCount++;
            rowUpdated = true;
          }
          
          // Add Zoom update if available
          if (zoomColIndex !== -1 && masterInfo.zoom !== null) {
            const range = `Sheet1!${columnIndexToLetter(zoomColIndex)}${weeklyIdx + 2}`;
            batchUpdates.push({
              range,
              values: [[masterInfo.zoom]]
            });
            updateCount++;
            rowUpdated = true;
          }
          
          // Add Vertical URL update. If the column exists in the weekly sheet,
          // its value will be set to the master sheet's value (even if blank).
          if (verticalUrlColIndex !== -1) { // Condition changed here
            const range = `Sheet1!${columnIndexToLetter(verticalUrlColIndex)}${weeklyIdx + 2}`;
            batchUpdates.push({
              range,
              values: [[masterInfo.verticalUrl]] // This will write a blank string if masterInfo.verticalUrl is blank
            });
            updateCount++;
            rowUpdated = true;
          }
          
          if (rowUpdated) {
            console.log(`[syncVideoDataFromMaster] Will update data for "${weeklySong}" by "${weeklyArtist}"`);
          }
       } else {
          console.log(`[syncVideoDataFromMaster] No match found in master sheet for "${weeklySong}" by "${weeklyArtist}"`);
        }
      }

      // Apply all updates in a single batch - ONE API CALL for all updates
      if (batchUpdates.length > 0) {
        console.log(`[syncVideoDataFromMaster] Applying ${batchUpdates.length} updates in a single batch request`);
        await sheetsService.batchUpdateValues(weeklySpreadsheetId, batchUpdates);
        console.log(`[syncVideoDataFromMaster] Successfully updated ${batchUpdates.length} cells with latest video data (${updateCount} updates across ${matchCount} songs)`);
       } else {
        console.log('[syncVideoDataFromMaster] No updates needed from master sheet');
      }
    } catch (err) {
      console.error('[syncVideoDataFromMaster] Error syncing video data from master sheet:', err);
    }
  }, []); // No dependencies needed as it doesn't use any component state directly

  // Now wrap loadData in useCallback to prevent it from changing on every render
  const loadData = useCallback(async (sheetName: string): Promise<SpreadsheetData | null> => {
    try {
      if (!selectedSpreadsheet) {
        console.error('[loadData] No spreadsheet selected');
        return null;
      }
      
      console.log(`[loadData] Loading data from spreadsheet ${selectedSpreadsheet}, sheet ${sheetName}...`);
      
      setLoading(true);
      // Get the data from Google Sheets
      const result = await sheetsService.getData(selectedSpreadsheet, `${sheetName}!A1:Z1000`);
      
      if (!result.values || result.values.length === 0) {
        console.error('[loadData] No data found in spreadsheet');
        setError('No data found in spreadsheet');
        return null;
      }
      
      console.log(`[loadData] Loaded ${result.values.length} rows`);
      
      // Get header row first
      const headers = result.values[0];
      
      // Get all other rows (data rows)
      const rows = result.values.slice(1);

      const newData: SpreadsheetData = {
        headers,
        rows,
        originalHeaders: [...headers],
        originalRows: rows.map(row => [...row])
      };

      console.log(`[loadData] Processed data with ${headers.length} columns and ${rows.length} rows`);
      
      // Instead of reading from cell B28, get the chart date from the spreadsheet filename
      try {
        if (setChartDate) {
          // Fetch the spreadsheet metadata to get its name
          const file = await sheetsService.getFileInfo(selectedSpreadsheet);
          console.log(`[loadData] Got file info:`, file);
          
          if (file && file.name) {
            // Parse date from filename if it matches the pattern (e.g., "5-3-25")
            const dateMatch = file.name.match(/^(\d+)-(\d+)-(\d+)$/);
            if (dateMatch) {
              const [, month, day, year] = dateMatch;
              // Convert month number to month name
              const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                              'July', 'August', 'September', 'October', 'November', 'December'];
              const monthIndex = parseInt(month) - 1;
              const fullYear = parseInt(year) < 100 ? 2000 + parseInt(year) : parseInt(year);
              
              // Format the date (e.g., "May 3, 2025")
              const formattedDate = `${monthNames[monthIndex]} ${parseInt(day)}, ${fullYear}`;
              
              console.log(`[loadData] Extracted chart date from filename: ${formattedDate}`);
              setChartDate(formattedDate);
              
              // Also update cell B28 to keep it in sync
              try {
                await sheetsService.updateData(selectedSpreadsheet, 'Sheet1!B28', [[formattedDate]]);
                console.log(`[loadData] Updated B28 with formatted date: ${formattedDate}`);
              } catch (updateErr) {
                console.error('[loadData] Error updating B28:', updateErr);
                // Continue even if the update fails
              }
            } else {
              console.log(`[loadData] Filename does not match expected date format: ${file.name}`);
            }
          } else {
            console.log(`[loadData] Could not get file name for spreadsheet ${selectedSpreadsheet}`);
          }
        } else {
          console.log(`[loadData] setChartDate prop is not available, skipping chart date update`);
        }
      } catch (dateError) {
        console.error('[loadData] Error getting chart date from filename:', dateError);
        // Don't fail the whole load if just the date fetch fails
      }
      
      // Process and save the data
      setData(newData);
      generateAutocompleteOptions(newData);

      // Sync when new data is loaded - pass both required arguments
      await syncVideoDataFromMaster(selectedSpreadsheet, newData); 
      
      return newData;
    } catch (err) {
      console.error('[loadData] Error loading data:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to load data';
      setError(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  }, [selectedSpreadsheet, setData, setError, setLoading, syncVideoDataFromMaster, setChartDate]); // Specify all dependencies

  // Fix the loadChartData function to remove the redundant syncVideoDataFromMaster call
  const loadChartData = async () => {
    try {
      console.log('[loadChartData] Starting chart data load...');
      if (!selectedSpreadsheet) {
        console.error('[loadChartData] No spreadsheet selected');
        return;
      }
      
      await loadData('Sheet1');
      
      // IMPORTANT: Removed redundant syncVideoDataFromMaster call here 
      // It's already called inside loadData
      
    } catch (err) {
      console.error('[loadChartData] Error loading chart data:', err);
    }
  };

  const handleDateChange = async (newDate: Date | null) => {
    if (!newDate || !isValid(newDate)) {
      console.log('[handleDateChange] Invalid date selected:', newDate);
      setError('Invalid date selected');
      return;
    }
    
    const formattedDate = getFormattedDate(newDate);
    console.log('[handleDateChange] Processing date:', formattedDate);
    
    const isAvailable = availableDates.some(availableDate => 
      getFormattedDate(availableDate) === formattedDate
    );
    console.log('[handleDateChange] Is date available:', isAvailable);
    
    try {
      setLoading(true);
      setError(null);
      
      const files = await sheetsService.listFiles(folderId);
      console.log('[handleDateChange] Available files:', files);
      
      const matchingSpreadsheet = files.find(file => file.name === formattedDate);
      console.log('[handleDateChange] Matching spreadsheet:', matchingSpreadsheet);
      
      if (matchingSpreadsheet) {
        console.log('[handleDateChange] Loading existing spreadsheet:', matchingSpreadsheet.id);
        setSelectedSpreadsheet(matchingSpreadsheet.id);
        localStorage.setItem('currentSpreadsheetId', matchingSpreadsheet.id);
        onDateChange(newDate);
        
        // Load the data
        await loadData('Sheet1');
        
        // Format date more nicely for the title
        const readableDate = formatChartDate(newDate);
        
        // Update cell B28 with the date
        console.log(`[handleDateChange] Updating cell B28 with formatted date: ${readableDate}`);
        try {
          await sheetsService.updateData(matchingSpreadsheet.id, 'Sheet1!B28', [[readableDate]]);
          
          // Directly update the app title
          if (setChartDate) {
            console.log(`[handleDateChange] Directly updating chart date in App title: ${readableDate}`);
            setChartDate(readableDate);
          }
        } catch (updateErr) {
          console.error('[handleDateChange] Error updating B28:', updateErr);
        }
      } else if (isSaturday(newDate)) {
        console.log('[handleDateChange] Creating new spreadsheet for Saturday');
        
        try {
          // Instead of using template, duplicate the most recent sheet
          console.log('[handleDateChange] Finding most recent sheet to duplicate');
          const newSpreadsheetId = await sheetsService.duplicateAndRenameSheet(folderId, formattedDate);
          console.log('[handleDateChange] Created new spreadsheet by duplication:', newSpreadsheetId);
          
          setSelectedSpreadsheet(newSpreadsheetId.id);
          localStorage.setItem('currentSpreadsheetId', newSpreadsheetId.id);
          onDateChange(newDate);
          
          // Format date more nicely for the title
          const readableDate = formatChartDate(newDate);
          
          // Update cell B28 with the date
          console.log(`[handleDateChange] Updating cell B28 with formatted date: ${readableDate}`);
          try {
            await sheetsService.updateData(newSpreadsheetId.id, 'Sheet1!B28', [[readableDate]]);
            
            // Directly update the app title
            if (setChartDate) {
              console.log(`[handleDateChange] Directly updating chart date in App title: ${readableDate}`);
              setChartDate(readableDate);
            }
          } catch (updateErr) {
            console.error('[handleDateChange] Error updating B28:', updateErr);
          }
          
          // Refresh available dates
          const updatedFiles = await sheetsService.listFiles(folderId);
          const updatedDates = updatedFiles
            .map(file => {
              const date = parse(file.name.trim(), 'M-d-yy', new Date());
              return isValid(date) ? date : null;
            })
            .filter((date): date is Date => date !== null);
          setAvailableDates(updatedDates);
          
          await loadData('Sheet1');
        } catch (duplicationError) {
          console.error('[handleDateChange] Error duplicating sheet:', duplicationError);
          // Fall back to template if duplication fails
          console.log('[handleDateChange] Falling back to template');
          
        const templateFile = files.find(file => file.name === 'Weekly Template');
        if (!templateFile) {
          throw new Error('Weekly Template not found');
        }

        const { id: newSpreadsheetId } = await sheetsService.create(formattedDate, folderId);
          console.log('[handleDateChange] Created new spreadsheet from template:', newSpreadsheetId);
        
        const templateData = await sheetsService.getData(templateFile.id, 'Sheet1!A1:Z1000');
        if (templateData.values) {
          const transformedValues = templateData.values;
          await sheetsService.updateData(newSpreadsheetId, 'Sheet1!A1:Z1000', transformedValues);
        }

        setSelectedSpreadsheet(newSpreadsheetId);
        localStorage.setItem('currentSpreadsheetId', newSpreadsheetId);
        onDateChange(newDate);
        
        const updatedFiles = await sheetsService.listFiles(folderId);
        const updatedDates = updatedFiles
          .map(file => {
            const date = parse(file.name.trim(), 'M-d-yy', new Date());
            return isValid(date) ? date : null;
          })
          .filter((date): date is Date => date !== null);
        setAvailableDates(updatedDates);
        
        await loadData('Sheet1');
      }
      }
    } catch (err) {
      console.error('[handleDateChange] Error:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to load data';
      setError(errorMessage);
      setSelectedSpreadsheet(null);
    } finally {
      setLoading(false);
    }
  };

  const handleCellClick = (row: number, col: number, value: string) => {
    if (!isSignedIn) return;
    
    // Check if this is the YouTube Link column or S3 URL column
    const columnName = data?.headers[col];
    if (columnName?.toLowerCase().includes('youtube link')) {
      setEditingUrl({ row, col, url: value });
      setEditingCell(null);
      setEditingS3Url(null);
    } else if (columnName?.toLowerCase() === 's3 url') {
      setEditingS3Url({ row, col, url: value });
      setEditingCell(null);
      setEditingUrl(null);
    } else {
      setEditingCell({ row, col, value });
      setEditingUrl(null);
      setEditingS3Url(null);
    }
  };

  const handleCellEdit = async (row: number, col: number, value: string) => {
    if (!data || !selectedDate || !selectedSpreadsheet) {
      console.error('Missing required data for cell edit:', { data, selectedDate, selectedSpreadsheet });
      setError('Unable to update cell: missing required data');
      return;
    }
    
    try {
      // Get the current value of the cell
      const currentValue = data.rows[row][col];
      
      // Only update if the value has changed
      if (value !== currentValue) {
        // Always use Sheet1 as the sheet name
        const range = `Sheet1!${String.fromCharCode(65 + col)}${row + 2}`; // +2 because row 0 is headers
        const values = [[value]];
        
        console.log('Attempting to update cell with:', { range, value, spreadsheetId: selectedSpreadsheet });
        await sheetsService.updateData(selectedSpreadsheet, range, values);
        
        // Add to change history
        setChangeHistory(prev => [...prev, {
          row,
          col,
          oldValue: currentValue,
          newValue: value,
          range
        }]);
        
        // Reload data to reflect changes
        console.log('Update successful, reloading data...');
        const updatedData = await loadData('Sheet1');
        if (!updatedData) {
          throw new Error('Failed to reload data after successful update');
        }
        
        // Clear any existing error
        setError(null);
      } else {
        console.log('Value unchanged, skipping update');
      }
    } catch (error) {
      console.error('Error in handleCellEdit:', error);
      
      // Extract the most meaningful error message
      let errorMessage = 'Failed to update cell';
      if (error instanceof Error) {
        errorMessage = error.message.replace(/^Failed to update cell: /, '');
        if (!errorMessage.includes('update') && !errorMessage.includes('cell')) {
          errorMessage = `Failed to update cell: ${errorMessage}`;
        }
      } else {
        errorMessage = `Failed to update cell: ${String(error)}`;
      }
      
      setError(errorMessage);
    } finally {
      setEditingCell(null);
    }
  };

  const handleUrlSave = async (url: string) => {
    if (!editingUrl || !data || !selectedDate || !selectedSpreadsheet) return;
    
    const { row, col } = editingUrl;
    // Always use Sheet1 as the sheet name
    const range = `Sheet1!${String.fromCharCode(65 + col)}${row + 2}`; // +2 because row 0 is headers
    const values = [[url]];
    
    try {
      await sheetsService.updateData(selectedSpreadsheet, range, values);
      await loadData('Sheet1');
      setEditingUrl(null);
    } catch (error) {
      console.error('Error updating URL:', error);
      if (error instanceof Error) {
        setError(`Failed to update URL: ${error.message}`);
      } else {
        setError('Failed to update URL. Please try again.');
      }
    }
  };

  const handleS3UrlSave = async (url: string) => {
    if (!editingS3Url || !data || !selectedDate || !selectedSpreadsheet) return;
    
    const { row, col } = editingS3Url;
    const range = `Sheet1!${String.fromCharCode(65 + col)}${row + 2}`;
    const values = [[url]];
    
    try {
      await sheetsService.updateData(selectedSpreadsheet, range, values);
      await loadData('Sheet1');
      setEditingS3Url(null);
    } catch (error) {
      console.error('Error updating S3 URL:', error);
      if (error instanceof Error) {
        setError(`Failed to update S3 URL: ${error.message}`);
      } else {
        setError('Failed to update S3 URL. Please try again.');
      }
    }
  };

  const handleSignIn = async () => {
    try {
      await sheetsService.signIn();
      setIsSignedIn(true);
      
      const files = await sheetsService.listFiles(folderId);
      
      if (files.length > 0) {
        const dates = files
          .map(file => {
            const date = parse(file.name.trim(), 'M-d-yy', new Date());
            return isValid(date) ? { date, id: file.id } : null;
          })
          .filter((item): item is { date: Date; id: string } => item !== null)
          .sort((a, b) => b.date.getTime() - a.date.getTime()); // Sort by date descending

        setAvailableDates(dates.map(item => item.date));
        
        // Always select the most recent date
        const mostRecent = dates[0];
        if (mostRecent) {
          setSelectedSpreadsheet(mostRecent.id);
          onDateChange(mostRecent.date);
          await loadData('Sheet1');
        }
      }
    } catch (err) {
      console.error('Error signing in:', err);
      setError(err instanceof Error ? err.message : 'Failed to sign in');
    }
  };

  const handleCreateSheet = async (date: Date, prefillPreviousWeek: boolean) => {
    try {
      setLoading(true);
      setError(null);

      const newSheetName = getFormattedDate(date);
      const { id: newSpreadsheetId } = await sheetsService.create(newSheetName, folderId);

      if (prefillPreviousWeek) {
        const previousWeekDate = subWeeks(date, 1);
        const previousWeekName = getFormattedDate(previousWeekDate);
        const files = await sheetsService.listFiles(folderId);
        const previousSpreadsheet = files.find(s => s.name === previousWeekName);

        if (previousSpreadsheet) {
          const sheets = await sheetsService.getSheets(previousSpreadsheet.id);
          if (sheets.length > 0) {
            const firstSheet = sheets[0];
            const previousData = await sheetsService.getData(previousSpreadsheet.id, `${firstSheet}!A1:Z1000`);
            
            if (previousData.values) {
              const transformedValues = previousData.values;
              await sheetsService.updateData(newSpreadsheetId, 'Sheet1!A1:Z1000', transformedValues);
            }
          }
        }
      } else if (data?.headers) {
        const newHeaders = data.headers;
        await sheetsService.updateData(newSpreadsheetId, 'Sheet1!A1:Z1', [newHeaders]);
      }

      const files = await sheetsService.listFiles(folderId);
      const dates = files
        .map(file => {
          const date = parse(file.name.trim(), 'M-d-yy', new Date());
          return isValid(date) ? date : null;
        })
        .filter((date): date is Date => date !== null);

      setAvailableDates(dates);
      setSelectedSpreadsheet(newSpreadsheetId);
      onDateChange(date);
      await loadData('Sheet1');

    } catch (err) {
      console.error('Error creating sheet:', err);
      setError(err instanceof Error ? err.message : 'Failed to create sheet');
    } finally {
      setLoading(false);
    }
  };

  const shouldDisableDate = useCallback((date: Date) => {
    const formattedDateToCheck = getFormattedDate(date);
    
    // Disable all non-Saturday dates
    if (!isSaturday(date)) return true;
    
    // For Saturdays, check if they exist in availableDates
    const isAvailable = availableDates.some(availableDate => 
      getFormattedDate(availableDate) === formattedDateToCheck
    );
    return false; // Don't disable Saturdays, we'll handle them in the renderer
  }, [availableDates, getFormattedDate]);

  // Fix the loadVideoData function to properly populate videoData
  const loadVideoData = async () => {
    try {
      console.log('[loadVideoData] Fetching data from Video Manager spreadsheet');
      const response = await sheetsService.getData(VIDEO_MANAGER_SPREADSHEET_ID, 'Sheet1!A1:Z1000');
      
      if (!response.values?.length) {
        console.error('No data found in Video Manager spreadsheet');
        return;
      }

      const headers = response.values[0].map(h => String(h).toLowerCase().trim());
      const indices = {
        song: headers.findIndex(header => header.includes('song')),
        artist: headers.findIndex(header => header.includes('artist')),
        youtubeLink: headers.findIndex(header => header === 'youtube link'),
        voText: headers.findIndex(header => header === 'voice over text'),
      };

      if (indices.song === -1 || indices.artist === -1) {
        console.error('Could not find required Song or Artist columns in Video Manager');
        return;
      }

      const newVideoData: { [key: string]: VideoManagerData } = {};
      const uniqueArtists = new Set<string>();

      response.values.slice(1).forEach(row => {
        const songName = String(row[indices.song] ?? '').trim();
        const artistName = String(row[indices.artist] ?? '').trim();

        if (songName && artistName) {
          const key = normalizeSongArtistKey(songName, artistName);
          console.log(`[loadVideoData] Storing with key: "${key}" for "${songName} - ${artistName}"`);
          
          newVideoData[key] = {
            song: songName,
            artist: artistName,
            youtubeLink: indices.youtubeLink !== -1 ? String(row[indices.youtubeLink] ?? '').trim() : '',
            voiceOverText: indices.voText !== -1 ? String(row[indices.voText] ?? '').trim() : '',
          };
          uniqueArtists.add(artistName);
        }
      });

      setVideoData(newVideoData);
      setSongArtistSuggestions(Object.keys(newVideoData).map(key => {
          const data = newVideoData[key];
          return `${data.song} - ${data.artist}`;
      }).sort());
      setArtistSuggestions(Array.from(uniqueArtists).sort());
      
      console.log('[loadVideoData] Updated videoData from Video Manager:', Object.keys(newVideoData).length, 'entries');
      // Log all keys for debugging
      console.log('[loadVideoData] All normalized keys:', Object.keys(newVideoData));
    } catch (error) {
      console.error('[loadVideoData] Error loading video data:', error);
    }
  };

  // Update the useEffect for loading video data
  // Load video data when the component is initialized and authenticated
  useEffect(() => {
    if (isSignedIn) {
      console.log('[SpreadsheetViewer] User is signed in, loading video data...');
      loadVideoData();
    }
  }, [isSignedIn]); // Only depend on authentication state

  // Add a separate effect to reload video data when the selected spreadsheet changes
  useEffect(() => {
    if (isSignedIn && selectedSpreadsheet) {
      console.log('[SpreadsheetViewer] Selected spreadsheet changed, ensuring video data is loaded');
      // Check if videoData is already loaded
      if (Object.keys(videoData).length === 0) {
        console.log('[SpreadsheetViewer] Video data not loaded yet, loading now');
        loadVideoData();
      }
    }
  }, [isSignedIn, selectedSpreadsheet]); // Remove videoData to avoid circular dependency

  // Add undo function
  const handleUndo = async () => {
    if (changeHistory.length === 0 || !selectedSpreadsheet) return;

    try {
      setLoading(true);
      setError(null);

      // Get the last change
      const lastChange = changeHistory[changeHistory.length - 1];
      
      // Revert the change
      await sheetsService.updateData(
        selectedSpreadsheet,
        lastChange.range,
        [[lastChange.oldValue]]
      );

      // Remove the last change from history
      setChangeHistory(prev => prev.slice(0, -1));

      // Reload the data to reflect the change
      await loadData('Sheet1');

      setError(null);
    } catch (error) {
      console.error('Error in undo:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      setError(`Failed to undo: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const handleReorderSave = async (newOrder: number[]) => {
    if (!data || !selectedSpreadsheet) return;

    // Create a new array with the reordered rows
    const reorderedRows = newOrder.map((index) => data.rows[index]);

    // Find the position column index
    const positionColumnIndex = data.headers.findIndex(header => 
      header.toLowerCase().includes('position')
    );

    if (positionColumnIndex === -1) {
      setError('Could not find position column');
      return;
    }

    // Update the position numbers
    const updatedRows = reorderedRows.map((row, index) => {
      const newRow = [...row];
      newRow[positionColumnIndex] = (10 - index).toString();
      return newRow;
    });

    try {
      // Update the spreadsheet with the new order
      await sheetsService.updateData(selectedSpreadsheet, 'Sheet1!A2:Z11', updatedRows);
      // Reload the data to reflect changes
      await loadData('Sheet1');
    } catch (error) {
      console.error('Error updating order:', error);
      setError('Failed to update song order');
    }
  };

  const handleMoveSong = async (rowIndex: number, direction: 'up' | 'down') => {
    if (!data || !selectedSpreadsheet) return;
    
    const newRows = [...data.rows];
    const newPosition = direction === 'up' ? rowIndex - 1 : rowIndex + 1;
    
    if (newPosition < 0 || newPosition >= newRows.length) return;
    
    // Swap the rows
    [newRows[rowIndex], newRows[newPosition]] = [newRows[newPosition], newRows[rowIndex]];
    
    // Update the position numbers (10 at top, 1 at bottom)
    newRows.forEach((row, index) => {
      const positionIndex = data.headers.findIndex(header => 
        header.toLowerCase() === 'position'
      );
      if (positionIndex !== -1) {
        row[positionIndex] = (10 - index).toString();
      }
    });
    
    try {
      // Update the Google Sheet
      const range = `Sheet1!A2:Z${newRows.length + 1}`;
      console.log('Attempting to update with:', {
        spreadsheetId: selectedSpreadsheet,
        range,
        rows: newRows
      });
      await sheetsService.updateData(selectedSpreadsheet, range, newRows);
      
      // Update local state
      setData({ ...data, rows: newRows });
    } catch (error) {
      console.error('Detailed error updating song position:', error);
      if (error instanceof Error) {
        setError(`Failed to update song position: ${error.message}`);
      } else {
        setError('Failed to update song position. Please try again.');
      }
    }
  };

  const handleCancel = () => {
    // Reset all editing states
    setEditingCell(null);
    setEditingUrl(null);
    setEditingS3Url(null);
    
    // Clear the row data if we're editing a row
    if (data && editingCell) {
      const newRows = [...data.rows];
      newRows[editingCell.row] = newRows[editingCell.row].map(() => '');
      setData({ ...data, rows: newRows });
    }
  };

  const handleChartUpload = async (chartText: string) => {
    console.log('[handleChartUpload] Received chart text:', chartText);
    try {
      setLoading(true);
      // Store the chart text in case it's needed later by handleNewVideosSave
      setUploadedChartText(chartText); 
      
      // Process the chart text to get entries
      const chartEntries = await processChartText(chartText);
      console.log('[handleChartUpload] Processed entries:', chartEntries);

      // Call updateChartWithEntries directly
      // This function now fetches its own fresh video data.
      await updateChartWithEntries(chartEntries);
      
      setUploadChartDialogOpen(false); // Close the initial upload dialog
      setError(null);
    } catch (error) {
      console.error('Error processing chart upload:', error);
      setError('Failed to process chart upload. Please check the format or try again.');
      // Maybe don't close dialog on error? setUploadChartDialogOpen(false);
    } finally {
      setLoading(false);
    }
  };

  const getLastWeeksChartData = async (): Promise<{ [key: string]: { position: number; weeks: number } }> => {
    if (!selectedDate) return {};
    
    try {
      const lastWeekDate = subWeeks(selectedDate, 1);
      const lastWeekName = getFormattedDate(lastWeekDate);
      const files = await sheetsService.listFiles(folderId);
      const lastWeekSpreadsheet = files.find(s => s.name === lastWeekName);
      
      if (!lastWeekSpreadsheet) return {};
      
      const sheets = await sheetsService.getSheets(lastWeekSpreadsheet.id);
      if (sheets.length === 0) return {};
      
      const firstSheet = sheets[0];
      const lastWeekData = await sheetsService.getData(lastWeekSpreadsheet.id, `${firstSheet}!A1:Z1000`);
      
      if (!lastWeekData.values || lastWeekData.values.length === 0) return {};
      
      const headers = lastWeekData.values[0].map(String);
      const positionIndex = headers.findIndex(h => h.toLowerCase() === 'position');
      const songIndex = headers.findIndex(h => h.toLowerCase().includes('song'));
      const artistIndex = headers.findIndex(h => h.toLowerCase().includes('artist'));
      const weeksIndex = headers.findIndex(h => h.toLowerCase() === 'weeks');
      
      if (positionIndex === -1 || songIndex === -1 || artistIndex === -1) return {};
      
      const result: { [key: string]: { position: number; weeks: number } } = {};
      
      lastWeekData.values.slice(1).forEach(row => {
        const song = String(row[songIndex] || '').trim();
        const artist = String(row[artistIndex] || '').trim();
        const position = parseInt(String(row[positionIndex] || ''), 10);
        const weeks = weeksIndex !== -1 ? parseInt(String(row[weeksIndex] || ''), 10) : 0;
        
        if (song && artist && !isNaN(position)) {
          const key = `${song} - ${artist}`.toLowerCase();
          result[key] = { position, weeks };
        }
      });
      
      return result;
    } catch (error) {
      console.error('Error getting last week\'s chart data:', error);
      return {};
    }
  };

  // Modify function signature to accept the up-to-date video data map
  const updateChartWithEntries = async (
    entries: { position: number; lastWeek: number; song: string; artist: string; peak?: number; weeks?: number; message?: string }[] // Added message to type
  ) => {
    if (!selectedSpreadsheet || !data) return;

    console.log('[updateChartWithEntries] Received entries:', JSON.stringify(entries, null, 2)); // <-- Log 4

    try {
      // --- Fetch fresh video data inside the function --- 
      console.log('[updateChartWithEntries] Fetching fresh Video Manager data...');
      const freshVideoManagerData = await sheetsService.getData(VIDEO_MANAGER_SPREADSHEET_ID, 'Sheet1!A:D'); // Assuming A=Song, B=Artist, D=Link
      const freshVideoDataMap: { [key: string]: VideoManagerData } = {};
      if (freshVideoManagerData.values && freshVideoManagerData.values.length > 1) {
         const headers = freshVideoManagerData.values[0].map(h => String(h).toLowerCase().trim());
         const songIndex = headers.indexOf('song');
         const artistIndex = headers.indexOf('artist');
         const linkIndex = headers.indexOf('youtube link'); // Match exact header if possible

         if (songIndex !== -1 && artistIndex !== -1) {
             freshVideoManagerData.values.slice(1).forEach(row => {
                 const song = String(row[songIndex] ?? '').trim();
                 const artist = String(row[artistIndex] ?? '').trim();
                 if (song && artist) {
                     const key = normalizeSongArtistKey(song, artist); // Key from sheet data
                     // --- Log key being added to map ---
                     console.log(`[updateChartWithEntries MapBuild] Adding key from sheet: "${key}" for ${song} - ${artist}`);
                     freshVideoDataMap[key] = {
                         song: song,
                         artist: artist,
                         youtubeLink: linkIndex !== -1 ? String(row[linkIndex] ?? '').trim() : ''
                         // Add other fields if needed (like VO text)
                     };
                 }
             });
             console.log(`[updateChartWithEntries] Built fresh map with ${Object.keys(freshVideoDataMap).length} entries.`);

             // Update videoData state with the fresh map
             setVideoData(prevVideoData => ({
               ...prevVideoData, 
               ...freshVideoDataMap 
             }));
             console.log('[updateChartWithEntries] Merged freshVideoDataMap into videoData state.');

         } else {
             console.warn('[updateChartWithEntries] Could not find required Song/Artist columns in fresh Video Manager data.');
         }
      } else {
         console.log('[updateChartWithEntries] Fresh Video Manager data is empty or headers only.');
      }
       // --- End fetching fresh video data ---

      // --- Log all keys found in the fresh map ---
      console.log('[updateChartWithEntries] Keys in freshVideoDataMap:', Object.keys(freshVideoDataMap));

      // Get last week's chart data
      const lastWeekData = await getLastWeeksChartData();
      
      // Get current chart data state (for isNewEntry check - might still have timing issues)
      const currentEntries = new Set<string>();
      const currentSongIndex = data.headers.findIndex(h => h.toLowerCase().includes('song'));
      const currentArtistIndex = data.headers.findIndex(h => h.toLowerCase().includes('artist'));
      
      if (currentSongIndex !== -1 && currentArtistIndex !== -1) {
        data.rows.forEach(row => {
          const song = String(row[currentSongIndex] || '').trim();
          const artist = String(row[currentArtistIndex] || '').trim();
          if (song && artist) {
            currentEntries.add(normalizeSongArtistKey(song, artist)); 
          }
        });
      }
      
      // Sort entries from 10 to 1
      const sortedEntries = [...entries].sort((a, b) => b.position - a.position);
      
      // Find column indices once
      const positionIndex = data.headers.findIndex(h => h.toLowerCase() === 'position');
      const lastWeekIndex = data.headers.findIndex(h => h.toLowerCase().includes('last week'));
      const songIndex = data.headers.findIndex(h => h.toLowerCase().includes('song'));
      const artistIndex = data.headers.findIndex(h => h.toLowerCase().includes('artist'));
      const youtubeLinkIndex = data.headers.findIndex(h => h.toLowerCase().includes('youtube link'));
      const weeksIndex = data.headers.findIndex(h => h.toLowerCase() === 'weeks');
      const peakIndex = data.headers.findIndex(h => h.toLowerCase() === 'peak');
      const messageIndex = data.headers.findIndex(h => h.toLowerCase() === 'message');

      // Prepare batch updates for individual cells instead of entire rows
      const batchUpdates: Array<{ range: string; values: string[][] }> = [];
      
      sortedEntries.forEach((entry, index) => {
        const rowNumber = index + 2; // Start from row 2 (1-indexed)
        
        // Use normalized key for lookup
        const key = normalizeSongArtistKey(entry.song, entry.artist);
        console.log(`[updateChartWithEntries Lookup] Looking up key from parsed entry: "${key}" for ${entry.song} - ${entry.artist}`);
        
        // Use the FRESHLY FETCHED videoDataMap instead of the component state/prop
        const videoInfo = freshVideoDataMap[key]; 
        
        console.log(`[updateChartWithEntries] Processing Entry (Pos ${entry.position}):`, entry);
        console.log(`[updateChartWithEntries] Found videoInfo:`, videoInfo);
        
        // Add individual cell updates only for the columns we want to modify
        if (positionIndex !== -1) {
          batchUpdates.push({
            range: `Sheet1!${String.fromCharCode(65 + positionIndex)}${rowNumber}`,
            values: [[entry.position.toString()]]
          });
        }
        
        if (lastWeekIndex !== -1) {
          batchUpdates.push({
            range: `Sheet1!${String.fromCharCode(65 + lastWeekIndex)}${rowNumber}`,
            values: [[entry.lastWeek === 0 ? '-' : entry.lastWeek.toString()]]
          });
        }
        
        if (songIndex !== -1) {
          batchUpdates.push({
            range: `Sheet1!${String.fromCharCode(65 + songIndex)}${rowNumber}`,
            values: [[videoInfo?.song || entry.song]]
          });
        }
        
        if (artistIndex !== -1) {
          batchUpdates.push({
            range: `Sheet1!${String.fromCharCode(65 + artistIndex)}${rowNumber}`,
            values: [[videoInfo?.artist || entry.artist]]
          });
        }
        
        if (youtubeLinkIndex !== -1 && videoInfo?.youtubeLink) {
          batchUpdates.push({
            range: `Sheet1!${String.fromCharCode(65 + youtubeLinkIndex)}${rowNumber}`,
            values: [[videoInfo.youtubeLink]]
          });
        }
        
        if (weeksIndex !== -1 && entry.weeks !== undefined) {
          batchUpdates.push({
            range: `Sheet1!${String.fromCharCode(65 + weeksIndex)}${rowNumber}`,
            values: [[entry.weeks.toString()]]
          });
        }
        
        if (peakIndex !== -1 && entry.peak !== undefined) {
          batchUpdates.push({
            range: `Sheet1!${String.fromCharCode(65 + peakIndex)}${rowNumber}`,
            values: [[entry.peak.toString()]]
          });
        }
        
        if (messageIndex !== -1 && entry.message) {
          batchUpdates.push({
            range: `Sheet1!${String.fromCharCode(65 + messageIndex)}${rowNumber}`,
            values: [[entry.message]]
          });
        }
      });

      // Execute batch update for individual cells
      console.log(`[updateChartWithEntries] Executing ${batchUpdates.length} individual cell updates to preserve existing data`);
      if (batchUpdates.length > 0) {
        await sheetsService.batchUpdateValues(selectedSpreadsheet, batchUpdates);
      }
      
      // PUT loadData back here
      await loadData('Sheet1'); 
      // setError(null); // This was incorrectly uncommented in previous edit, keep it commented or handle appropriately
    } catch (error) {
      console.error('Error updating chart:', error);
      throw new Error('Failed to update chart with new data');
    }
  };

  // *** NEW HELPER FUNCTION ***
  const fetchAndBuildVideoDataMap = async (): Promise<Map<string, VideoManagerData>> => {
    console.log('[fetchAndBuildVideoDataMap] Fetching video data from manager sheet...');
    const map = new Map<string, VideoManagerData>();
    try {
      const result = await sheetsService.getData(VIDEO_MANAGER_SPREADSHEET_ID, 'Sheet1!A:D'); // Fetch Song, Artist, YT Link, Voice Over Text
      if (result.values && result.values.length > 1) { // Check if values exist and have more than just header
        result.values.slice(1).forEach(row => { // Skip header row
          const song = row[0] || '';
          const artist = row[1] || '';
          const youtubeLink = row[3] || ''; // Assuming YT link is in column D (index 3)
          const voiceOverText = row[4] || ''; // Assuming VO text is in column E (index 4) - adjust if needed
          if (song && artist) {
            const key = normalizeSongArtistKey(song, artist);
            map.set(key, { song, artist, youtubeLink, voiceOverText });
          }
        });
      }
      console.log(`[fetchAndBuildVideoDataMap] Built map with ${map.size} entries.`);
    } catch (error) {
      console.error('[fetchAndBuildVideoDataMap] Error fetching or building map:', error);
      // Optionally re-throw or handle error appropriately
    }
    return map;
  };

  // handleNewVideosSave signature needs to accept chartText
  const handleNewVideosSave = async (videos: Video[], chartText: string): Promise<void> => { // Explicitly type the return
    console.log('[handleNewVideosSave ENTRY] Function called. Videos received:', JSON.stringify(videos, null, 2));
    console.log('[handleNewVideosSave ENTRY] Received chartText length:', chartText?.length);

    try {
      // --- Simplified Fetching and Updating Logic ---
      console.log('[handleNewVideosSave] Fetching fresh video data from sheet...');
      // *** CALL THE NEW HELPER FUNCTION ***
      const freshVideoDataMap = await fetchAndBuildVideoDataMap();

      console.log(`[handleNewVideosSave] Found ${Object.keys(freshVideoDataMap).length} existing keys in sheet (Map size: ${freshVideoDataMap.size}).`); // Log Map size

      const videosToActuallyAdd: Video[] = [];
      const videosToUpdateLocally: Video[] = []; // Track videos that exist but might need local YT link update

      videos.forEach(video => {
        const key = normalizeSongArtistKey(video.song, video.artist);
        if (!freshVideoDataMap.has(key)) {
          console.log(`[handleNewVideosSave Filter] Key "${key}" NOT in sheet. Will add.`);
          videosToActuallyAdd.push(video);
        } else {
          console.log(`[handleNewVideosSave Filter] Key "${key}" EXISTS in sheet. Will update locally if needed.`);
          // Even if it exists, we might have added a YouTube link in the dialog
          videosToUpdateLocally.push(video);
        }
      });

      // --- Append to Sheet --- 
      if (videosToActuallyAdd.length > 0) {
        console.log(`[handleNewVideosSave] Appending ${videosToActuallyAdd.length} new videos to sheet.`);
        const VIDEO_MANAGER_SPREADSHEET_ID = '1aVv8xC1HIB1XQDDwF8wiLtkB9c-D_g4zPnhFhbizkTo'; // Consider making this a constant or prop
        // Fetch current row count *again* right before appending to be safer
        const currentSheetDataForCount = await sheetsService.getData(VIDEO_MANAGER_SPREADSHEET_ID, 'Sheet1!A:A'); // Only need one column for count
        const nextRow = (currentSheetDataForCount.values?.length || 0) + 1;

        const newRows = videosToActuallyAdd.map(video => [
          video.song,
          video.artist,
          '', // Placeholder
          video.youtubeLink || ''
        ]);
        const range = `Sheet1!A${nextRow}:D${nextRow + videosToActuallyAdd.length - 1}`;
        await sheetsService.updateData(VIDEO_MANAGER_SPREADSHEET_ID, range, newRows);
        await sheetsService.sortRange(VIDEO_MANAGER_SPREADSHEET_ID, 'Sheet1!A2:Z', [{ dimensionIndex: 0, sortOrder: 'ASCENDING' }]); // Sort after adding
        console.log('[handleNewVideosSave] Appended and sorted sheet.');
      } else {
        console.log('[handleNewVideosSave] No new videos to append to sheet.');
      }

      // --- Update Local State (Both new and existing potentially updated) --- 
      console.log('[handleNewVideosSave] Updating local videoData state...');
      setVideoData(prevVideoData => {
          const updatedData = { ...prevVideoData };
          // Add newly added videos
          videosToActuallyAdd.forEach(video => {
              const key = normalizeSongArtistKey(video.song, video.artist);
              updatedData[key] = {
                  song: video.song,
                  artist: video.artist,
                  youtubeLink: video.youtubeLink || ''
              };
          });
          // Update potentially changed existing videos (e.g., added youtubeLink)
          videosToUpdateLocally.forEach(video => {
              const key = normalizeSongArtistKey(video.song, video.artist);
              // Ensure we don't overwrite existing link with empty if dialog didn't provide one
              const existingLink = updatedData[key]?.youtubeLink;
              updatedData[key] = {
                  song: video.song,
                  artist: video.artist,
                  youtubeLink: video.youtubeLink || existingLink || '' // Prioritize new link, then existing, then empty
              };
          });
          return updatedData;
      });
      console.log('[handleNewVideosSave] Local videoData state updated.');

      // --- Process Chart --- 
      if (!chartText) {
        console.warn('[handleNewVideosSave] chartText is empty or undefined. Skipping chart processing.');
      } else {
        console.log('[handleNewVideosSave] Processing chart text parameter...');
        const chartEntries = await processChartText(chartText);
        console.log('[handleNewVideosSave] Passing these chartEntries to updateChartWithEntries:', JSON.stringify(chartEntries, null, 2));
        await updateChartWithEntries(chartEntries); // This function now uses the latest videoData state implicitly
      }

      console.log('[handleNewVideosSave SUCCESS] Function finished successfully.');

    } catch (error) {
      console.error('[handleNewVideosSave CATCH] Error saving new videos:', error);
      setError('Failed to save new videos. Please try again.');
    } finally {
       console.log('[handleNewVideosSave FINALLY] Add new video process complete.');
       // Dialog closing is handled by ChartUploadDialog now
    }
  };

  const handleVideoSelectedForChart = async (videoId: string) => {
    if (searchTargetRowIndex === null) return;
    
    console.log(`[SpreadsheetViewer] Video selected for chart row ${searchTargetRowIndex}, ID: ${videoId}`);
    setLoading(true);
    setError(null);

    try {
      const fullUrl = `https://www.youtube.com/watch?v=${videoId}`;
      
      // 1. Fetch official info
      console.log(`[SpreadsheetViewer] Fetching official info for ${fullUrl}`);
      const officialSongInfo = await youtubeService.getSongInfo(fullUrl);
      console.log(`[SpreadsheetViewer] Official info:`, officialSongInfo);

      // 2. Add to Video Manager Sheet (Keep existing logic)
      let addedToVideoManager = false;
      try {
         // ... (Existing logic to add to Video Manager Sheet) ...
         console.log(`[SpreadsheetViewer] Adding to Video Manager sheet...`);
         const videoManagerDataResponse = await sheetsService.getData(VIDEO_MANAGER_SPREADSHEET_ID, 'Sheet1!A:D'); 
         const nextRow = (videoManagerDataResponse.values?.length || 0) + 1;
         const videoManagerRange = `Sheet1!A${nextRow}:D${nextRow}`;
         const videoManagerValues = [[officialSongInfo.song, officialSongInfo.artist, '', fullUrl]]; 
         await sheetsService.updateData(VIDEO_MANAGER_SPREADSHEET_ID, videoManagerRange, videoManagerValues);
         console.log(`[SpreadsheetViewer] Added to Video Manager sheet at row ${nextRow}.`);
         addedToVideoManager = true; // Mark as successful

         // --- START: Immediate videoData State Update ---
         const newVideoKey = normalizeSongArtistKey(officialSongInfo.song, officialSongInfo.artist);
         // --- Log key before setting state --- 
         console.log(`[SpreadsheetViewer handleVideoSelected] Key used to update videoData state: "${newVideoKey}"`);
         const newEntryForVideoData: VideoManagerData = {
             song: officialSongInfo.song,
             artist: officialSongInfo.artist,
             youtubeLink: fullUrl,
             // voiceOverText: '' // Initialize if needed
         };
         setVideoData(prev => {
           console.log(`[SpreadsheetViewer] Updating local videoData state with key: "${newVideoKey}"`);
           return {...prev, [newVideoKey]: newEntryForVideoData };
         });
         console.log('[SpreadsheetViewer] Local videoData state updated.');
         // --- END: Immediate videoData State Update ---

         // We might not need to await this anymore if local state is updated
         // await loadVideoData(); // Consider removing await or the call entirely
         loadVideoData(); // Reload in background just in case

      } catch (vmError) {
         console.error('[SpreadsheetViewer] Error adding to Video Manager sheet:', vmError);
         // Optional: Decide if you want to proceed with chart update if VM fails
      }

      // 3. Update Current Chart Sheet (Only if add to VM was successful? Or always try?)
      // Let's proceed even if VM add failed for now, but ideally handle it.
      if (!selectedSpreadsheet) { 
         throw new Error('Current spreadsheet ID is missing.');
      }
      console.log(`[SpreadsheetViewer] Updating current chart sheet (row ${searchTargetRowIndex})...`);
      
      // --- Get ORIGINAL headers to find correct column indices --- 
      const headerData = await sheetsService.getData(selectedSpreadsheet, 'Sheet1!A1:Z1');
      if (!headerData.values || !headerData.values[0]) {
          throw new Error('Could not fetch original headers from the current sheet.');
      }
      const originalHeaders = headerData.values[0].map(String); // Ensure strings
      console.log('[SpreadsheetViewer] Original Headers:', originalHeaders);

      // Find indices in ORIGINAL headers
      const originalSongColIndex = originalHeaders.findIndex(h => h.toLowerCase().includes('song'));
      const originalArtistColIndex = originalHeaders.findIndex(h => h.toLowerCase().includes('artist'));
      // Use exact match for video column for more safety
      const originalVideoColIndex = originalHeaders.findIndex(h => h.toLowerCase() === 'youtube link'); 

      console.log(`[SpreadsheetViewer] Original Indices - Song: ${originalSongColIndex}, Artist: ${originalArtistColIndex}, Video: ${originalVideoColIndex}`);

      // Check if the video column index was found
      if (originalVideoColIndex === -1) {
           console.error("[SpreadsheetViewer] Could not find exact 'YouTube Link' column in original headers.");
           // Optionally try a broader search as fallback or throw error
           // const fallbackVideoIndex = originalHeaders.findIndex(h => h.toLowerCase().includes('video'));
           // if (fallbackVideoIndex === -1) throw new Error("Could not find video column.");
           // else originalVideoColIndex = fallbackVideoIndex;
      }

      const updates: { range: string; values: string[][] }[] = [];
      const sheetRow = searchTargetRowIndex + 2; // +2 for header and 0-indexing

      // Use ORIGINAL indices for range calculation
      if (originalSongColIndex !== -1) {
        updates.push({
          range: `Sheet1!${String.fromCharCode(65 + originalSongColIndex)}${sheetRow}`,
          values: [[officialSongInfo.song]]
        });
      }
      if (originalArtistColIndex !== -1) {
         updates.push({
          range: `Sheet1!${String.fromCharCode(65 + originalArtistColIndex)}${sheetRow}`,
          values: [[officialSongInfo.artist]]
        });
      }
      // Only add update if index was found
      if (originalVideoColIndex !== -1) { 
         const videoUpdateRange = `Sheet1!${String.fromCharCode(65 + originalVideoColIndex)}${sheetRow}`;
         console.log(`[SpreadsheetViewer] Calculated Video Update Range: ${videoUpdateRange}`); // Log the calculated range
         updates.push({
          range: videoUpdateRange,
          values: [[fullUrl]]
        });
      }

      if (updates.length > 0) {
        // Apply updates (individually for now)
        for (const update of updates) {
           console.log(`[SpreadsheetViewer] Updating chart ORIGINAL range ${update.range} with value ${update.values[0][0]}`);
           await sheetsService.updateData(selectedSpreadsheet, update.range, update.values);
        }
        console.log(`[SpreadsheetViewer] Current chart sheet updated.`);

        // --- START: Immediate Local State Update ---
        if (data && searchTargetRowIndex !== null) {
          const localVideoColIndex = data.headers.findIndex(h => h.toLowerCase() === 'youtube link');
          if (localVideoColIndex !== -1) {
            console.log(`[SpreadsheetViewer] Updating local data state for row ${searchTargetRowIndex}, col ${localVideoColIndex}`);
            const newData = { ...data }; // Clone the data object
            const newRows = [...newData.rows]; // Clone the rows array
            const targetRow = [...newRows[searchTargetRowIndex]]; // Clone the target row array
            targetRow[localVideoColIndex] = fullUrl; // Update the YouTube link
            newRows[searchTargetRowIndex] = targetRow; // Put the updated row back
            newData.rows = newRows; // Update the rows in the new data object
            setData(newData); // Set the state to trigger re-render
            console.log('[SpreadsheetViewer] Local data state updated with new YouTube link.');
          } else {
            console.warn('[SpreadsheetViewer] Could not find \'YouTube Link\' column in local filtered headers for immediate update.');
          }
        }
        // --- END: Immediate Local State Update ---

        // Reload data in the background to ensure consistency, but UI updated already
        loadData('Sheet1'); // No need to await this for UI responsiveness 
      } else {
         console.warn('[SpreadsheetViewer] No columns found in ORIGINAL headers to update in the current chart sheet.');
      }

    } catch (error) {
      console.error('[SpreadsheetViewer] Error in handleVideoSelectedForChart:', error);
      setError(error instanceof Error ? error.message : 'Failed to add video and update chart.');
    } finally {
      setLoading(false);
      setSearchDialogOpen(false);
      setSearchTargetRowIndex(null);
      setSearchInitialQuery('');
    }
  };

  // Add a loading tracker ref to prevent concurrent initializations
  const isInitializing = useRef(false);

  // Update the useEffect for initial loading to prevent duplicate initialization
  useEffect(() => {
    const initializeApp = async () => {
      // Prevent concurrent initializations
      if (isInitializing.current) {
        console.log('[initializeApp] Initialization already in progress, skipping duplicate call');
        return;
      }
      
      isInitializing.current = true;
      console.log('[initializeApp] Starting app initialization');
      
      try {
        await sheetsService.init();
        setIsInitialized(true);

        // Only continue if we're signed in
        if (!sheetsService.isSignedIn()) {
          console.log('[initializeApp] Not signed in, waiting for user login');
          isInitializing.current = false;
          return;
        }
        
        setIsSignedIn(true);
        
        // Load all available dates (do this only once)
        console.log('[initializeApp] Fetching files from folder:', folderId);
        
        // Cache the file list response to avoid redundant calls
          const files = await sheetsService.listFiles(folderId);
          console.log('Found files:', files.map(f => f.name));
          
        // Parse dates from filenames
          const dates = files
            .map(file => {
              const date = parse(file.name.trim(), 'M-d-yy', new Date());
              console.log('Parsing date from filename:', file.name, '→', date);
            return isValid(date) ? date : null;
          })
          .filter((date): date is Date => date !== null);
          
        setAvailableDates(dates);
        console.log('Parsed dates:', dates.map(d => getFormattedDate(d)));
        
        // Check for previously selected date
        const savedSpreadsheetId = localStorage.getItem('currentSpreadsheetId');
        if (savedSpreadsheetId) {
          // Find the corresponding date
          const matchingFile = files.find(file => file.id === savedSpreadsheetId);
          if (matchingFile) {
            const date = parse(matchingFile.name.trim(), 'M-d-yy', new Date());
            if (isValid(date)) {
              console.log('Initializing with existing selected date:', matchingFile.name);
              setSelectedSpreadsheet(savedSpreadsheetId);
              onDateChange(date);
              
              // Important: Only load data once the component is fully initialized
              // Use setTimeout to break potential dependency cycles
              setTimeout(() => {
                loadData('Sheet1');
              }, 0);
            }
          }
        }
      } catch (err) {
        console.error('[initializeApp] Error initializing app:', err);
        setError('Failed to initialize the application');
      } finally {
        isInitializing.current = false;
      }
    };

    initializeApp();
  }, [folderId, onDateChange, loadData, getFormattedDate]); // Add missing dependencies

  // USE EFFECT: Load data when selected spreadsheet changes (and user is signed in)
  useEffect(() => {
    // Only run if initialization is complete, user is signed in, and a sheet is selected
    if (isInitialized && isSignedIn && selectedSpreadsheet && !isInitializing.current) {
      console.log(`[LoadDataEffect] Selected spreadsheet changed to ${selectedSpreadsheet}. Loading data...`);
      loadData('Sheet1').then(loadedData => {
        if (loadedData && selectedSpreadsheet !== VIDEO_MANAGER_SPREADSHEET_ID) {
           console.log(`[LoadDataEffect] Data loaded for ${selectedSpreadsheet}. Now syncing from master...`);
           // Sync after data is loaded
           syncVideoDataFromMaster(selectedSpreadsheet, loadedData);
        } else {
            console.log(`[LoadDataEffect] Skipping sync for master sheet or if data failed to load.`);
        }
      });
    } else {
        console.log(`[LoadDataEffect] Skipping load. isInitialized: ${isInitialized}, isSignedIn: ${isSignedIn}, selectedSpreadsheet: ${selectedSpreadsheet}, isInitializing: ${isInitializing.current}`);
    }
  }, [selectedSpreadsheet, isInitialized, isSignedIn, loadData, syncVideoDataFromMaster, VIDEO_MANAGER_SPREADSHEET_ID]);

  // Add a new handler for the render button
  const handleRender = async () => {
    if (!selectedDate || !selectedSpreadsheet) {
      setError('Please select a valid date and sheet first.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const formattedDate = getFormattedDate(selectedDate);
      const targetRenderSheetName = `${formattedDate}-render`;
      console.log(`[Output] Looking for render sheet: ${targetRenderSheetName}`);

      // List files in the Renders folder
      const renderFiles: DriveFile[] = await sheetsService.listFiles(RENDERS_FOLDER_ID);
      let renderSheetId: string | null = null;

      const existingRenderSheet = renderFiles.find((file: DriveFile) => file.name === targetRenderSheetName);

      if (existingRenderSheet) {
        console.log(`[Output] Found existing render sheet: ${existingRenderSheet.id}`);
        renderSheetId = existingRenderSheet.id;
      } else {
        console.log(`[Output] Render sheet not found. Attempting to copy latest.`);
        // Find the most recent existing -render sheet to copy
        const allRenderSheets = renderFiles
          .map((file: DriveFile) => {
            if (!file.name?.endsWith('-render')) return null;
            const datePart = file.name.replace('-render', '');
            const date = parse(datePart, 'M-d-yy', new Date());
            // Ensure file.id is treated as string
            return isValid(date) ? { id: String(file.id), date: date, name: String(file.name) } : null;
          })
          // Type the filter callback parameter
          .filter((item): item is { id: string; date: Date; name: string } => item !== null)
          // Type the sort callback parameters
          .sort((a: { date: Date }, b: { date: Date }) => b.date.getTime() - a.date.getTime()); 

        if (allRenderSheets.length === 0) {
          throw new Error('Cannot create render sheet: No previous -render sheets found in the folder to copy from.');
        }

        const latestRenderSheet = allRenderSheets[0];
        console.log(`[IntroOutro] Found latest sheet to copy: ${latestRenderSheet.name} (ID: ${latestRenderSheet.id})`);

        // Copy the latest sheet (ensure latestRenderSheet.id is string)
        const copiedFile = await sheetsService.copyFile(latestRenderSheet.id, RENDERS_FOLDER_ID, true);
        console.log(`[IntroOutro] Copied sheet with permissions, new ID: ${copiedFile.id}, temporary name: ${copiedFile.name}`);

        // Rename the copy (ensure copiedFile.id is string)
        await sheetsService.renameFile(copiedFile.id, targetRenderSheetName);
        renderSheetId = copiedFile.id;
        console.log(`[IntroOutro] Renamed copied sheet to: ${targetRenderSheetName}`);
      }

      // Navigate to the Intro/Outro page with the sheet ID and name
      if (renderSheetId) {
        // Use URL parameter for the ID, but also pass sheet name and date through state
        // This gives us both direct URL access and the context needed for page title
        navigate(`/intro-outro/${renderSheetId}`, {
          state: { 
            sheetName: targetRenderSheetName,
            formattedDate: formatChartDate(selectedDate)
          }
        });
      }

    } catch (err) {
      console.error('[IntroOutro] Error handling render sheet: ', err);
      setError(err instanceof Error ? err.message : 'Failed to find or create render sheet.');
    } finally {
      setLoading(false);
    }
  };
  // ---> END: Add handler for Intro/Outro Button

  // Add the missing generateAutocompleteOptions function
  const generateAutocompleteOptions = (data: SpreadsheetData): void => {
    if (!data) return;

    const songColIndex = data.headers.findIndex(h => h.toLowerCase().includes('song'));
    const artistColIndex = data.headers.findIndex(h => h.toLowerCase().includes('artist'));

    if (songColIndex === -1 || artistColIndex === -1) return;
    
    const uniqueArtists = new Set<string>();
    const songArtistOptions: string[] = [];
    
    data.rows.forEach(row => {
      const song = String(row[songColIndex] || '').trim();
      const artist = String(row[artistColIndex] || '').trim();
      
      if (song && artist) {
        uniqueArtists.add(artist);
        songArtistOptions.push(`${song} - ${artist}`);
      }
    });
    
    setSongArtistSuggestions(songArtistOptions.sort());
    setArtistSuggestions(Array.from(uniqueArtists).sort());
  };

  // --- MODIFIED handleExportSocial ---
  const handleExportSocial = async () => {
    if (!selectedSpreadsheet || !selectedDate) {
      enqueueSnackbar('No spreadsheet or date selected', { variant: 'error' });
      return;
    }
    
    if (isExportingSocial) {
      enqueueSnackbar('Export already in progress. Please wait.', { variant: 'warning' });
      return;
    }
    
    setIsExportingSocial(true);
    
    try {
      // Get the sheet name from the selected date
      const formattedSheetName = format(selectedDate, 'M-d-yy');
      
      // Validate data
      if (!data || !data.headers || !data.rows) {
        throw new Error('No data loaded. Please refresh and try again.');
      }
      
      // Find required column indices
      const indices = {
        position: data.headers.findIndex(h => h.toLowerCase() === 'position'),
        s3Url: data.headers.findIndex(h => h.toLowerCase().includes('s3 url')),
        voIn: data.headers.findIndex(h => h.toLowerCase() === 'vo in'),
        voOut: data.headers.findIndex(h => h.toLowerCase() === 'vo out'),
        inPoint: data.headers.findIndex(h => h.toLowerCase() === 'in point'),
        duration: data.headers.findIndex(h => h.toLowerCase() === 'duration'),
        artist: data.headers.findIndex(h => h.toLowerCase() === 'artist'),
        // More specific song column detection to avoid finding the wrong column
        song: (() => {
          // Define possible song column headers
          const songHeaders = ['song', 'title', 'song title'];
          // First try exact match
          const exactMatch = data.headers.findIndex(h => 
            songHeaders.includes(h.toLowerCase())
          );
          if (exactMatch !== -1) return exactMatch;
          
          // Fall back to includes match
          return data.headers.findIndex(h => 
            songHeaders.some(songHeader => h.toLowerCase().includes(songHeader))
          );
        })(),
        verticalUrl: data.headers.findIndex(h => h.toLowerCase() === 'vertical url')
      };
      
      // Define required indices
      const requiredIndices = {
        position: indices.position,
        s3Url: indices.s3Url,
        voIn: indices.voIn,
        voOut: indices.voOut,
        inPoint: indices.inPoint,
        duration: indices.duration,
        artist: indices.artist,
        song: indices.song
      };
      const missing = Object.entries(requiredIndices).filter(([, val]) => val === -1).map(([key]) => `'${key}'`);
      if (missing.length > 0) throw new Error(`Missing required columns: ${missing.join(', ')}`);

      console.log('[Export Social] Checking chart data for songs that need vertical URLs');
      
      // Process the weekly chart data and filter out songs that already have vertical URLs
      const clipInstructions = data.rows
        .map(row => {
          const position = String(row[indices.position] || '').trim();
          const s3Url = String(row[indices.s3Url] || '').trim();
          const artist = String(row[indices.artist] || '').trim();
          const song = String(row[indices.song] || '').trim();
          const voIn = String(row[indices.voIn] || '');
          const voOut = String(row[indices.voOut] || '');
          const inPoint = String(row[indices.inPoint] || '');
          const duration = String(row[indices.duration] || '');
          
          // Check if this position already has a vertical URL
          const verticalUrl = indices.verticalUrl !== -1 ? String(row[indices.verticalUrl] || '').trim() : '';
          const hasVerticalUrl = verticalUrl !== '';
          
          return {
            position,
            s3Url,
            voIn,
            voOut,
            inPoint,
            duration,
            artist,
            song,
            hasVerticalUrl
          };
        })
        .filter(instr => { 
          // Basic validation - needs to be a position 1-10 with S3 URL, artist and song
          const posNum = parseInt(instr.position, 10); 
          const isValidPosition = !isNaN(posNum) && posNum >= 1 && posNum <= 10;
          const hasRequiredFields = instr.s3Url && instr.artist && instr.song;
          
          // Skip if already has a vertical URL
          if (instr.hasVerticalUrl) {
            console.log(`[Export Social] Skipping position ${instr.position} - "${instr.song}" by "${instr.artist}" already has a Vertical URL`);
            return false;
          }
          
          return isValidPosition && hasRequiredFields;
        });

      // Remove the hasVerticalUrl property before sending to backend
      const cleanedInstructions = clipInstructions.map(({ hasVerticalUrl, ...rest }) => rest);

      if (cleanedInstructions.length === 0) {
        throw new Error('No valid clip instructions found. All eligible positions already have Vertical URLs or are missing required data.');
      }
      
      console.log(`[Export Social] Sending ${cleanedInstructions.length} instructions to backend.`);
      // Add detailed logging of the actual data being sent to the server
      console.log('[Export Social] DETAILED DATA BEING SENT TO SERVER:');
      cleanedInstructions.forEach((clip, index) => {
        console.log(`  [${index+1}] Position: ${clip.position}, Artist: "${clip.artist}", Song: "${clip.song}", S3 URL: ${clip.s3Url}`);
      });
      
      // Let user know we're skipping some positions
      const totalValidPositions = data.rows.filter(row => {
        const posNum = parseInt(String(row[indices.position] || '').trim(), 10);
        return !isNaN(posNum) && posNum >= 1 && posNum <= 10;
      }).length;
      
      if (cleanedInstructions.length < totalValidPositions) {
        const skippedCount = totalValidPositions - cleanedInstructions.length;
        if (skippedCount > 0) {
          enqueueSnackbar(`Skipping ${skippedCount} position(s) that already have Vertical Videos.`, { variant: 'info' });
        }
      }

      // Make API Call with weekly sheet ID
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/generate-social-clips`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sheetName: formattedSheetName, 
          clipInstructions: cleanedInstructions,
          weeklySheetId: selectedSpreadsheet
        }),
      });

      // --- Handle NEW API Response ---
      if (response.status === 202) { 
         const { jobId } = await response.json();
         console.log(`[Export Social] Job started successfully. Job ID: ${jobId}`);
         setSocialExportJobId(jobId);

         // Show persistent snackbar
         const key = enqueueSnackbar('Vertical clip generation started...', {
             variant: 'info',
             persist: true, 
         });
         setProgressSnackbarKey(key);
         setLastSnackbarMessage('Vertical clip generation started...'); // Set initial message

         // Start polling
         // Call immediately first time, then set interval
         pollJobStatus(jobId, key); 
         const interval = setInterval(() => {
             // Need to check if the key still exists in case it was closed by user
             // Although it's persistent, manual close is possible
             // Re-fetch the key? Or just pass it. Let's pass it.
             pollJobStatus(jobId, key); 
         }, 1500); // Poll more frequently (1.5 seconds) to catch all messages
         setPollIntervalId(interval);

      } else { 
         const responseText = await response.text(); // Log raw text
         console.error('[Export Social] API request failed. Raw response text:', responseText); // Log raw text
         try {
           const parsedErrorData = JSON.parse(responseText); // Renamed variable
           console.error('[Export Social] API request failed (parsed from text):', response.status, parsedErrorData);
           enqueueSnackbar(`Failed to start export: ${parsedErrorData.error || response.statusText}`, { variant: 'error' });
         } catch (e) {
           console.error('[Export Social] Failed to parse raw response text as JSON:', e);
           enqueueSnackbar(`Failed to start export: Failed to parse error response. Status: ${response.status}`, { variant: 'error' });
         }
         setIsExportingSocial(false); 
      }

    } catch (error: any) { 
      console.error('[Export Social] Error in handleExportSocial:', error);
      enqueueSnackbar(`Error preparing export: ${error.message}`, { variant: 'error' });
      setIsExportingSocial(false); 
    }
  };

  const renderCell = (value: string, header: string, rowIndex: number, colIndex: number) => {
    const isPositionColumn = header.toLowerCase() === 'position';
    const isEditable = !isPositionColumn && !['youtube link', 'artwork url', 's3 url', 'voice over text', 'voice over url'].includes(header.toLowerCase());
    const isCenterAligned = ['last week', 'this week', 'peak', 'weeks on', 'duration'].some(term => header.toLowerCase().includes(term));
    const isNumeric = ['last week', 'this week', 'peak', 'weeks on', 'duration'].some(term => header.toLowerCase().includes(term));
    const isThumbnail = header.toLowerCase() === 'thumbnail';
    const isVideoTitle = header.toLowerCase() === 'video title';
    const isArtist = header.toLowerCase() === 'artist';
    const isLabel = header.toLowerCase() === 'label';
    const isDuration = header.toLowerCase() === 'duration';
    const isInPoint = header.toLowerCase() === 'in point';
    const isZoom = header.toLowerCase() === 'zoom';
    const isVoiceOverText = header.toLowerCase() === 'voice over text';
    const isVoiceOverUrl = header.toLowerCase() === 'voice over url';
    const isArtworkUrl = header.toLowerCase() === 'artwork url';
    const isS3Url = header.toLowerCase() === 's3 url';
    const isMusicVideo = header.toLowerCase() === 'youtube link';
    const isSong = header.toLowerCase().includes('song');

    if (isPositionColumn) {
      return (
        <Typography variant="body2" sx={{
            textAlign: 'center',
            width: '60px',
            padding: '4px',
            cursor: 'default',
            userSelect: 'none',
          fontWeight: 'bold',
            '&:hover': {
              backgroundColor: 'transparent'
            }
        }}>
            {rowIndex + 1}
          </Typography>
      );
    }

    // --- Special handling for Last Week column to show '-' for 0 --- 
    const isLastWeekColumn = header.toLowerCase().includes('last week');
    if (isLastWeekColumn && editingCell?.row !== rowIndex && editingCell?.col !== colIndex) {
      const displayValue = (value === '0' || value === '') ? '-' : value;
      return (
        <Box sx={{ 
          display: 'block',
          width: '100%',
          pt: '4px',
          textAlign: 'center', // Center align Last Week
          cursor: isSignedIn ? 'pointer' : 'default' // Add pointer cursor to indicate it's editable
        }}>
          {displayValue}
        </Box>
      );
    }
    // --- End of Last Week handling ---

    if (editingCell?.row === rowIndex && editingCell?.col === colIndex) {
      if (isSong) {
        return (
          <Autocomplete
            freeSolo
            options={songArtistSuggestions}
            value={editingCell.value}
            onChange={(event, newValue) => {
              console.log('Song Autocomplete onChange:', newValue);
              handleSongSelect(rowIndex, colIndex, newValue);
            }}
            onInputChange={(event, newInputValue, reason) => {
              if (reason === 'input') {
                setEditingCell(prev => prev ? { ...prev, value: newInputValue } : null);
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                autoFocus
                size="small"
                onBlur={() => {
                  console.log('Song Autocomplete Blur. Value:', editingCell?.value);
                  if (editingCell && !songArtistSuggestions.includes(editingCell.value)) {
                    handleCellEdit(editingCell.row, editingCell.col, editingCell.value);
                  } else if (editingCell && !editingCell.value) {
                    handleCellEdit(editingCell.row, editingCell.col, '');
                  }
                  setEditingCell(null);
                }}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && editingCell) {
                    e.preventDefault();
                    handleSongSelect(editingCell.row, editingCell.col, editingCell.value);
                  }
                }}
              />
            )}
            sx={{ 
              minWidth: 200,
              '& .MuiInputBase-root': {
                padding: '0px'
              }
            }}
          />
        );
      } else if (isArtist) {
        return (
          <Autocomplete
            freeSolo
            options={artistSuggestions}
            value={editingCell.value}
            onChange={(event, newValue) => {
              if (newValue !== null) {
                handleCellEdit(rowIndex, colIndex, newValue);
              }
              setEditingCell(null);
            }}
            onInputChange={(event, newInputValue, reason) => {
              if (reason === 'input') {
                setEditingCell(prev => prev ? { ...prev, value: newInputValue } : null);
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                autoFocus
                size="small"
                onBlur={() => {
                  if (editingCell) {
                    handleCellEdit(editingCell.row, editingCell.col, editingCell.value);
                  }
                  setEditingCell(null);
                }}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && editingCell) {
                    e.preventDefault();
                    handleCellEdit(editingCell.row, editingCell.col, editingCell.value);
                  }
                }}
              />
            )}
            sx={{ 
              minWidth: 200,
              '& .MuiInputBase-root': {
                padding: '0px'
              }
            }}
          />
        );
      } else if (isZoom) {
        return (
          <Box sx={{ 
            display: 'flex !important', 
            justifyContent: 'center !important',
            width: '100% !important',
            height: '100% !important',
            alignItems: 'center !important',
            padding: '0 !important',
            margin: '0 !important',
            position: 'relative !important',
            top: '50% !important',
            transform: 'translateY(-50%) !important'
          }}>
            <Checkbox
              checked={value.toUpperCase() === 'TRUE'}
              onChange={(e) => handleCellEdit(rowIndex, colIndex, e.target.checked ? 'TRUE' : 'FALSE')}
              onClick={(e) => e.stopPropagation()}
              sx={{
                margin: '0 !important',
                padding: '0 !important',
                '& .MuiSvgIcon-root': {
                  fontSize: '1.75rem !important'  // Increased and made consistent size
                }
              }}
            />
          </Box>
        );
      } else {
        return (
          <input
            type="text"
            value={editingCell.value}
            onChange={(e) => setEditingCell({ ...editingCell, value: e.target.value })}
            onBlur={() => {
              if (editingCell) {
                handleCellEdit(editingCell.row, editingCell.col, editingCell.value);
              }
            }}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && editingCell) {
                handleCellEdit(editingCell.row, editingCell.col, editingCell.value);
              }
            }}
            autoFocus
            style={{ width: '100%' }}
          />
        );
      }
    } else if (isSong) {
      // Find the Voice Over Text column
      const voiceOverColIndex = data?.headers.findIndex(header => 
        header.toLowerCase().includes('voice over text')
      );
      const voiceOverText = typeof voiceOverColIndex === 'number' && voiceOverColIndex !== -1 && data?.rows && data.rows[rowIndex] ? 
        data.rows[rowIndex][voiceOverColIndex] : '';

      return (
        <Box sx={{ 
          display: 'flex',
          flexDirection: 'column',
          gap: 1
        }}>
          <Box sx={{ 
            cursor: isSignedIn ? 'pointer' : 'default',
            color: value ? 'text.primary' : 'text.disabled',
            width: '100%',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}>
            {value}
          </Box>
          {editingCell?.row === rowIndex && editingCell?.col === voiceOverColIndex ? (
            <TextField
              value={editingCell.value}
              onChange={(e) => setEditingCell({ ...editingCell, value: e.target.value })}
              onBlur={() => {
                if (editingCell) {
                  handleCellEdit(editingCell.row, editingCell.col, editingCell.value);
                }
              }}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && editingCell) {
                  handleCellEdit(editingCell.row, editingCell.col, editingCell.value);
                }
              }}
              autoFocus
              size="small"
              fullWidth
              placeholder={`${value} VO`}
              sx={{ 
                '& .MuiInputBase-root': {
                  padding: '0px',
                  fontSize: '0.875rem',
                  width: '200%',
                  position: 'relative',
                  left: '-50%'
                }
              }}
            />
          ) : (
            <Box 
              sx={{ 
                cursor: isSignedIn ? 'pointer' : 'default',
                color: voiceOverText ? 'text.secondary' : 'text.disabled',
                fontSize: '0.875rem',
                width: 'auto',
                minWidth: '100%',
                whiteSpace: 'nowrap',
                overflow: 'visible',
                textOverflow: 'unset',
                mt: 0.5,
                fontStyle: !voiceOverText ? 'italic' : 'normal',
                paddingRight: '0px',
                position: 'relative',
                display: 'block',
                zIndex: 1
              }}
              onClick={(e) => {
                e.stopPropagation(); // Keep stopPropagation
                if (isSignedIn) {
                  // Navigate to VO Manager with song/artist as query params
                  if (value) {
                    // Get the artist from the row data if possible
                    const artistColIndex = data?.headers.findIndex(h => h.toLowerCase().includes('artist'));
                    // Only access data.rows[rowIndex][artistColIndex] if all values are defined
                    let rowArtist = '';
                    if (artistColIndex !== undefined && artistColIndex !== -1 && data?.rows && data.rows[rowIndex]) {
                      rowArtist = data.rows[rowIndex][artistColIndex] || '';
                    }
                    // Keep this as VO Manager since it's specifically for voice over content
                    navigate(`/vo-manager?song=${encodeURIComponent(value)}&artist=${encodeURIComponent(rowArtist)}&play=true`);
                  }
                }
              }}
            >
              {voiceOverText || `${value} VO`}
            </Box>
          )}
        </Box>
      );
    } else if (isMusicVideo) {
      // Look up the song and artist for this row
      const songColumnIndex = data?.headers.findIndex(h => h.toLowerCase().includes('song'));
      const artistColumnIndex = data?.headers.findIndex(h => h.toLowerCase().includes('artist'));
      
      // Only proceed if we can find song and artist columns
      if (songColumnIndex !== -1 && artistColumnIndex !== -1 && data?.rows[rowIndex]) {
        const songName = data.rows[rowIndex][songColumnIndex as number] || '';
        const artistName = data.rows[rowIndex][artistColumnIndex as number] || '';
        
        const key = normalizeSongArtistKey(songName, artistName);
        console.log(`[renderCell] Row ${rowIndex}: Song="${songName}", Artist="${artistName}", Generated Key="${key}"`);
        
        // Try to find video info in our videoData map
        const videoInfo = videoData[key];
        console.log(`[renderCell] Found videoInfo for key "${key}":`, videoInfo);
        
        // Get YouTube ID either from the cell value or from videoData
        const videoId = getYouTubeVideoId(value) || (videoInfo?.youtubeLink ? getYouTubeVideoId(videoInfo.youtubeLink) : null);
        
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
            {videoId ? (
              <img
                src={`https://img.youtube.com/vi/${videoId}/mqdefault.jpg`}
                alt="Video thumbnail"
                style={{
                  width: '120px',
                  height: '68px',
                  objectFit: 'cover',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
                onClick={() => {
                  if (songName && artistName) {
                    // Set a flag in sessionStorage before navigating
                    sessionStorage.setItem('navigatedFromCharts', 'true');
                    // Navigate to Video Manager with song/artist params and player parameter
                    navigate(`/video-manager?song=${encodeURIComponent(songName)}&artist=${encodeURIComponent(artistName)}&player=true`);
                  } else if (videoInfo?.youtubeLink) {
                    window.open(videoInfo.youtubeLink, '_blank');
                  }
                }}
              />
            ) : (
              <IconButton 
                onClick={() => handleRequestVideoAdd(rowIndex)}
                sx={{ 
                  p: 1,
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.08)'
                  }
                }}
              >
                <AddCircleOutlineIcon sx={{ fontSize: '1.5rem' }} />
              </IconButton>
            )}
          </Box>
        );
      }
      
      // Fallback if we can't find song/artist
      const videoIdFromCell = getYouTubeVideoId(value);
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
          {videoIdFromCell ? (
            <img
              src={`https://img.youtube.com/vi/${videoIdFromCell}/mqdefault.jpg`}
              alt="Video thumbnail"
              style={{
                width: '120px',
                height: '68px',
                objectFit: 'cover',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
              onClick={() => {
                if (value) {
                  window.open(value, '_blank');
                }
              }}
            />
          ) : (
            <IconButton 
              onClick={() => handleRequestVideoAdd(rowIndex)}
              sx={{ 
                p: 1,
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.08)'
                }
              }}
            >
              <AddCircleOutlineIcon sx={{ fontSize: '1.5rem' }} />
            </IconButton>
          )}
        </Box>
      );
    } else if (isVoiceOverText) {
      // Skip rendering Voice Over Text in its own column since it's now under the song
      return null;
    } else if (isZoom) {
      return (
          <Box sx={{ 
            display: 'flex !important', 
            justifyContent: 'center !important',
            width: '100% !important',
            height: '100% !important',
            alignItems: 'center !important',
            padding: '0 !important',
            margin: '0 !important',
            position: 'relative !important',
            top: '50% !important',
            transform: 'translateY(-50%) !important'
          }}>
            <Checkbox
              checked={value.toUpperCase() === 'TRUE'}
              onChange={(e) => handleCellEdit(rowIndex, colIndex, e.target.checked ? 'TRUE' : 'FALSE')}
              onClick={(e) => e.stopPropagation()}
              sx={{
                margin: '0 !important',
                padding: '0 !important',
                '& .MuiSvgIcon-root': {
                  fontSize: '1.75rem !important'  // Increased and made consistent size
                }
              }}
            />
          </Box>
      );
    } else {
      return (
        <Box sx={{ 
          display: 'block',
          width: '100%',
          pt: '4px'
        }}>
          {value}
        </Box>
      );
    }
  };

  // Restore the handleSongSelect function
  const handleSongSelect = async (rowIndex: number, songColIndex: number, selectedValue: string | null) => {
    if (!selectedValue || !data || !selectedSpreadsheet) {
      // If cleared or required data missing, potentially clear related cells or just update song
      if (data && selectedSpreadsheet) {
        // Update the song cell to empty
        await handleCellEdit(rowIndex, songColIndex, '');
        
        // Also clear related fields (artist, last week, peak, weeks)
        const artistColIndex = data.headers.findIndex(h => h.toLowerCase().includes('artist'));
        const lastWeekColIndex = data.headers.findIndex(h => h.toLowerCase().includes('last week'));
        const peakColIndex = data.headers.findIndex(h => h.toLowerCase().includes('peak'));
        const weeksColIndex = data.headers.findIndex(h => h.toLowerCase().includes('weeks'));
        
        // Prepare batch updates to clear all related fields
        const updates = [];
        
        if (artistColIndex !== -1) {
          updates.push({
            range: `Sheet1!${String.fromCharCode(65 + artistColIndex)}${rowIndex + 2}`,
            values: [['']]
          });
        }
        
        if (lastWeekColIndex !== -1) {
          updates.push({
            range: `Sheet1!${String.fromCharCode(65 + lastWeekColIndex)}${rowIndex + 2}`,
            values: [['']]
          });
        }
        
        if (peakColIndex !== -1) {
          updates.push({
            range: `Sheet1!${String.fromCharCode(65 + peakColIndex)}${rowIndex + 2}`,
            values: [['']]
          });
        }
        
        if (weeksColIndex !== -1) {
          updates.push({
            range: `Sheet1!${String.fromCharCode(65 + weeksColIndex)}${rowIndex + 2}`,
            values: [['']]
          });
        }
        
        // Execute all updates
        try {
          for (const update of updates) {
            await sheetsService.updateData(selectedSpreadsheet, update.range, update.values);
          }
          
          // Reload data to reflect changes
          if (updates.length > 0) {
            await loadData('Sheet1');
          }
        } catch (error) {
          console.error('Error clearing related fields:', error);
          setError(error instanceof Error ? `Failed to clear related fields: ${error.message}` : 'Failed to clear related fields');
        }
      }
      setEditingCell(null); // Close editor
      return;
    }

    // 1. Parse Song and Artist from the selected "Song - Artist" string
    const parts = selectedValue.split(' - ');
    const songName = parts[0]?.trim();
    // Handle cases where artist might have '-' in their name
    const artistName = parts.slice(1).join(' - ').trim();

    if (!songName || !artistName) {
      console.warn('Could not parse song/artist from:', selectedValue);
      // Just save the raw selected value to the song cell if parsing fails
      await handleCellEdit(rowIndex, songColIndex, selectedValue);
      setEditingCell(null);
      return;
    }

    // 2. Create the lookup key
    const videoKey = normalizeSongArtistKey(songName, artistName);

    // 3. Find data in videoData state
    const match = videoData[videoKey];

    console.log(`[handleSongSelect] Row: ${rowIndex}, Selection: "${selectedValue}", Parsed Song: "${songName}", Parsed Artist: "${artistName}", Match found:`, match);

    // 4. Find column indices in the currently displayed sheet
    const artistColIndex = data.headers.findIndex(h => h.toLowerCase().includes('artist'));
    // Find YT Link and VO Text indices IF you want to auto-populate them
    const youtubeLinkColIndex = data.headers.findIndex(h => h.toLowerCase().includes('music video')); // Check header name matches display
    // const voTextColIndex = data.headers.findIndex(h => h.toLowerCase().includes('voice over text')); // Removed unused

    // 5. Prepare updates for Google Sheets
    const updates = [];
    // Always update the Song column with ONLY the parsed songName
    updates.push({
      range: `Sheet1!${String.fromCharCode(65 + songColIndex)}${rowIndex + 2}`,
      values: [[songName]] // <-- Save only the song name part here
    });

    // If a match was found in videoData and the Artist column exists...
    if (match && artistColIndex !== -1) {
      // Update Artist column
      updates.push({
        range: `Sheet1!${String.fromCharCode(65 + artistColIndex)}${rowIndex + 2}`,
        values: [[match.artist || '']] // Use artist from videoData
      });
    } else if (artistColIndex !== -1) {
      console.log("[handleSongSelect] No videoData match or Artist column not found. Only updating Song.");
    }

    // Optional: Update YouTube Link if column exists and data available
    if (match?.youtubeLink && youtubeLinkColIndex !== -1) {
      updates.push({
        range: `Sheet1!${String.fromCharCode(65 + youtubeLinkColIndex)}${rowIndex + 2}`,
        values: [[match.youtubeLink]]
      });
    }

    // 6. Execute updates (individually)
    try {
      setLoading(true); // Indicate activity
      console.log('[handleSongSelect] Sending individual updates:', updates);

      for (const update of updates) {
        console.log(`[handleSongSelect] Updating range: ${update.range} with value:`, update.values[0][0]);
        await sheetsService.updateData(selectedSpreadsheet, update.range, update.values);
      }

      // Reload data to show changes reflected from the sheet
      console.log('[handleSongSelect] Updates successful, reloading data...');
      await loadData('Sheet1');
      setError(null);
    } catch (error) {
      console.error('Error in handleSongSelect update loop:', error);
      setError(error instanceof Error ? `Failed to update row: ${error.message}` : 'Failed to update row');
    } finally {
      setLoading(false);
      setEditingCell(null); // Close the editor
    }
  };

  // Restore the handleRequestVideoAdd function
  const handleRequestVideoAdd = (rowIndex: number) => {
    if (!data) return;

    const songColIndex = data.headers.findIndex(h => h.toLowerCase().includes('song'));
    const artistColIndex = data.headers.findIndex(h => h.toLowerCase().includes('artist'));

    if (songColIndex !== -1 && artistColIndex !== -1) {
      const song = data.rows[rowIndex]?.[songColIndex] || '';
      const artist = data.rows[rowIndex]?.[artistColIndex] || '';
      const query = `${song} ${artist}`.trim();

      console.log(`[SpreadsheetViewer] Requesting video add for row ${rowIndex}: "${query}"`);
      setSearchInitialQuery(query);
      setSearchTargetRowIndex(rowIndex);
      setSearchDialogOpen(true);
    } else {
      console.error('[SpreadsheetViewer] Cannot find Song or Artist column index.');
      setError('Could not initiate video search: Song or Artist column missing.');
    }
  };

  // Restore the handleLogout function
  const handleLogout = async () => {
    try {
      await sheetsService.signOut();
      setIsSignedIn(false);
      // Clear any sensitive data
      setData(null);
      setSelectedSpreadsheet(null);
      setAvailableDates([]);
      setVideoData({});
    } catch (error) {
      console.error('Error signing out:', error);
      setError('Failed to sign out. Please try again.');
    }
  };

  const handleOutputManager = async () => {
    if (!selectedSpreadsheet) {
      enqueueSnackbar('No spreadsheet selected', { variant: 'error' });
      return;
    }
    
    localStorage.setItem('currentSpreadsheetId', selectedSpreadsheet);
    // Navigate with parentSpreadsheetId as query parameter
    navigate(`/output-manager?parentSpreadsheetId=${selectedSpreadsheet}`);
  };

  // --- NEW: Handler for INTRO/OUTRO button navigation ---
  const handleIntroOutroNavigation = async () => {
    if (!selectedDate || !selectedSpreadsheet) {
      setError('Please select a valid date and chart spreadsheet first.');
      enqueueSnackbar('Please select a valid date and chart spreadsheet first.', { variant: 'warning' });
      return;
    }

    setLoading(true);
    setError(null);
    let foundRenderId: string | null = null;

    try {
      console.log(`[IntroOutroNav] Getting name for weekly sheet: ${selectedSpreadsheet}`);
      const weeklySheetName = await sheetsService.getFileName(selectedSpreadsheet);
      if (!weeklySheetName) {
          throw new Error(`Could not retrieve the name for the weekly sheet ID: ${selectedSpreadsheet}`);
      }
      console.log(`[IntroOutroNav] Weekly sheet name: ${weeklySheetName}`);

      const targetRenderSheetName = `${weeklySheetName}-render`;
      console.log(`[IntroOutroNav] Searching for render sheet: ${targetRenderSheetName} in folder ${RENDERS_FOLDER_ID}`);

      // Try to find the existing render sheet by name
      foundRenderId = await sheetsService.findFileByName(targetRenderSheetName, RENDERS_FOLDER_ID);

      if (foundRenderId) {
        console.log(`[IntroOutroNav] Found existing render sheet: ${foundRenderId}`);
      } else {
        console.log(`[IntroOutroNav] Render sheet "${targetRenderSheetName}" not found. Attempting to copy latest.`);

        // Find the most recent existing -render sheet in the Renders folder to copy
        const renderFiles = await sheetsService.listFiles(RENDERS_FOLDER_ID);
        const allRenderSheets = renderFiles
          .map((file: DriveFile) => {
            // Removed check/use of file.modifiedTime
            if (!file.name?.endsWith('-render') || !file.id) return null;
            const datePart = file.name.replace('-render', '');
            const date = parse(datePart, 'M-d-yy', new Date()); // Only parse from name
            return isValid(date) ? { id: String(file.id), date: date, name: String(file.name) } : null;
          })
          .filter((item): item is { id: string; date: Date; name: string } => item !== null)
          .sort((a: { date: Date }, b: { date: Date }) => b.date.getTime() - a.date.getTime());

        if (allRenderSheets.length === 0) {
          throw new Error('Cannot create render sheet: No previous "-render" sheets found in the folder to copy from.');
        }

        const latestRenderSheet = allRenderSheets[0];
        console.log(`[IntroOutroNav] Found latest render sheet to copy: ${latestRenderSheet.name} (ID: ${latestRenderSheet.id})`);

        // Copy the latest sheet
        const copiedFile = await sheetsService.copyFile(latestRenderSheet.id, RENDERS_FOLDER_ID); // Permissions usually inherited
        console.log(`[IntroOutroNav] Copied sheet, new ID: ${copiedFile.id}, temp name: ${copiedFile.name}`);

        // Rename the copy
        if (!copiedFile.id) {
             throw new Error("Copy operation did not return a file ID.");
        }
        await sheetsService.renameFile(copiedFile.id, targetRenderSheetName);
        foundRenderId = copiedFile.id; // Use the ID of the newly created and renamed sheet
        console.log(`[IntroOutroNav] Renamed copied sheet to: ${targetRenderSheetName}. New ID: ${foundRenderId}`);
      }

      // Navigate if we have an ID
      if (foundRenderId) {
        console.log(`[IntroOutroNav] Navigating to /intro-outro/${foundRenderId}`);
        // Pass sheet name and date via state for potentially better UX on the target page
         const readableDate = formatChartDate(selectedDate!);
         navigate(`/intro-outro/${foundRenderId}`, {
             state: {
                 sheetName: targetRenderSheetName,
                 formattedDate: readableDate
             }
         });
      } else {
        // This case should ideally not be reached if the copy/rename logic works
        throw new Error("Failed to obtain a valid Render Sheet ID after search/copy attempt.");
      }

    } catch (err) {
      console.error('[IntroOutroNav] Error finding/creating render sheet: ', err);
      const errorMsg = err instanceof Error ? err.message : 'Failed to prepare the Intro/Outro sheet.';
      setError(errorMsg);
      enqueueSnackbar(errorMsg, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };
  // --- END NEW HANDLER ---

  if (!isInitialized) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isSignedIn) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <Button variant="contained" onClick={handleSignIn}>
          Sign in with Google
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      mt: 0,
      mx: '5%',
      width: '90vw',
      maxWidth: '90vw',
      boxSizing: 'border-box',
      overflow: 'hidden',
      position: 'relative',
      left: '50%',
      transform: 'translateX(-50%)',
    }}>
      <Box sx={{ 
        mb: 1, 
        display: 'flex', 
        gap: 1, 
        alignItems: 'center',
        px: 0.5,
        width: '100%',
        justifyContent: 'space-between' // This pushes left and right groups apart
      }}>
        {/* Left Group */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: { xs: 1, sm: 0 } }}>
        <DatePicker
          label="Week Ending"
          value={selectedDate}
          onChange={handleDateChange}
          disabled={!isSignedIn}
          sx={{ 
            width: 180,
            '& .MuiInputLabel-root': {
              backgroundColor: 'white',
              padding: '0 4px',
              transform: 'translate(14px, 16px) scale(0.75)',
              '&.Mui-focused': {
                transform: 'translate(14px, 16px) scale(0.75)'
              },
              zIndex: 1,
              position: 'relative'
            },
            '& .MuiOutlinedInput-root': {
              position: 'relative',
              zIndex: 0,
              padding: '8px 14px',
              '& .MuiOutlinedInput-notchedOutline': {
                paddingRight: '0'
              }
            }
          }}
          shouldDisableDate={shouldDisableDate}
          slots={{
            day: CustomDay
          }}
          slotProps={{
            textField: {
              size: 'small'
            }
          }}
        />
          {/* Start of reordered button block */}
          <Button // Upload Chart
            variant="outlined"
          onClick={() => setUploadChartDialogOpen(true)}
          disabled={!isSignedIn}
          size="small"
          sx={{
            height: '40px',
            padding: '4px 16px',
              textTransform: 'none', 
              fontWeight: 600, 
              color: 'black', // Black text
              borderColor: 'black', // Black outline
              '&:hover': { 
                borderColor: 'black', // Keep black outline on hover
                backgroundColor: 'rgba(0, 0, 0, 0.04)', // Standard light gray background tint on hover
              },
            marginTop: '22px'
          }}
        >
          Upload Chart
        </Button>
          <Button // VO Manager
            variant="outlined"
          onClick={() => navigate('/vo-manager')}
          disabled={!isSignedIn}
          size="small"
          sx={{
            height: '40px',
            padding: '4px 16px',
              textTransform: 'none', 
              fontWeight: 600, 
              color: 'black', // Black text
              borderColor: 'black', // Black outline
              '&:hover': { 
                borderColor: 'black', // Keep black outline on hover
                backgroundColor: 'rgba(0, 0, 0, 0.04)', // Standard light gray background tint on hover
              },
            marginTop: '22px'
          }}
        >
          VO Manager
        </Button>
          {/* Add Intro/Outro Button here */}
          <Button // INTRO/OUTRO
            onClick={handleIntroOutroNavigation}
            variant="outlined"
          size="small"
          sx={{
            height: '40px',
            padding: '4px 16px',
              textTransform: 'none', 
              fontWeight: 600, 
              color: 'black', // Black text
              borderColor: 'black', // Black outline
            '&:hover': {
                borderColor: 'black', // Keep black outline on hover
                backgroundColor: 'rgba(0, 0, 0, 0.04)', // Standard light gray background tint on hover
              },
              marginTop: '22px'
            }}
          >
            Intro/Outro
        </Button>
          {/* Video Manager Button removed from here */}
        </Box>

        {/* Right-aligned group */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: { xs: 1, sm: 0 } }}>
           {/* ... Undo Icon, Logout Icon, OUTPUT MANAGER Button ... */}
        <IconButton 
          onClick={handleUndo}
          disabled={!isSignedIn || changeHistory.length === 0}
          size="small"
          title="Undo last change"
            sx={{ marginTop: '16px' }} // Keep vertical alignment consistent
        >
          <UndoIcon />
        </IconButton>
        <IconButton 
          onClick={handleLogout}
          disabled={!isSignedIn}
          size="small"
          title="Sign out"
            sx={{ marginTop: '16px' }} // Keep vertical alignment consistent
        >
          <LogoutIcon />
        </IconButton>
          {/* Video Manager Button and OUTPUT MANAGER button are swapped back here */}
          <Button // Video Manager (moved back before OUTPUT MANAGER)
            variant="contained"
            onClick={() => navigate('/video-manager')}
            disabled={!isSignedIn}
            size="small"
            sx={{
              height: '40px',
              padding: '4px 16px',
              display: 'flex',
              alignItems: 'center',
              marginTop: '22px',
              backgroundColor: '#000000',
              color: '#00FF9A',
              '&:hover': {
                backgroundColor: '#1A1A1A',
                color: '#00FF9A',
              },
            }}
          >
            Video Manager
          </Button>
          <Button // OUTPUT MANAGER (moved back after Video Manager)
            variant="contained"
            onClick={handleOutputManager}
            disabled={!isSignedIn || loading}
            size="small"
            sx={{
              height: '40px',
              padding: '4px 16px',
              display: 'flex',
              alignItems: 'center',
              marginTop: '22px', // Keep existing margin for alignment
              textTransform: 'none',
              fontWeight: 600,
              backgroundColor: '#00FF9A',
              color: '#000000',
              '&:hover': {
                backgroundColor: '#00e68a',
                color: '#000000',
              },
            }}
          >
            OUTPUT MANAGER
          </Button>
        </Box>
      </Box>

      <CreateSheetDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onCreateSheet={handleCreateSheet}
        existingDates={availableDates}
        handleCancel={handleCancel}
      />

      <UrlDialog
        open={editingUrl !== null}
        onClose={() => setEditingUrl(null)}
        url={editingUrl?.url || ''}
        onSave={handleUrlSave}
      />

      <UrlDialog
        open={editingS3Url !== null}
        onClose={() => setEditingS3Url(null)}
        url={editingS3Url?.url || ''}
        onSave={handleS3UrlSave}
        title="Edit S3 URL"
      />

      <ChartUploadDialog
        open={uploadChartDialogOpen}
        onClose={() => setUploadChartDialogOpen(false)}
        onUpload={handleChartUpload}
        videoData={videoData}
        // Update the wrapper function to accept and pass chartText with types
        onDetailsSave={async (videosFromDialog: Video[], textFromDialog: string) => {
          console.log('[SpreadsheetViewer onDetailsSave PROP] Triggered! Text length:', textFromDialog?.length);
          await handleNewVideosSave(videosFromDialog, textFromDialog);
        }}
      />

      {/* Add the YouTube Search Dialog */}
      <YouTubeSearchDialog
         open={searchDialogOpen}
         onClose={() => {
             setSearchDialogOpen(false);
             setSearchTargetRowIndex(null);
             setSearchInitialQuery('');
         }}
         onSelect={handleVideoSelectedForChart}
         initialQuery={searchInitialQuery}
      />

      {error ? (
        <Alert severity="error" sx={{ mt: 1, mx: 0.5 }}>{error}</Alert>
      ) : loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <CircularProgress />
        </Box>
      ) : data ? (
        <TableContainer 
          component={Paper}
          sx={{
            width: '100%',
            maxWidth: '100%',
            overflowX: 'auto',
            '& .MuiTable-root': {
              width: '100%',
              tableLayout: 'fixed',
              display: 'table'
            },
            '& .MuiTableBody-root': {
              display: 'table-row-group'
            },
            '& .MuiTableRow-root': {
              display: 'table-row',
              height: '106px'  // Consistent height for all rows
            },
            '& .MuiTableCell-root': {
              display: 'table-cell',
              px: 0.5,
              py: 0.5,
              fontSize: '0.875rem',
              verticalAlign: 'top',
              paddingTop: '16px',  // Consistent top padding
              '&:first-of-type, &:last-of-type': {
                px: 0.5
              }
            },
            '& .sticky-column': {
              position: 'sticky',
              zIndex: 1,
              '&.thumbnail': { 
                left: '160px', 
                width: '187px', 
                padding: '16px 0',
                borderLeft: 'none',
                borderRight: 'none'
              },
              '&.position': { left: 0, width: '160px' }
            },
            '& .non-sticky-columns': {
              paddingLeft: '347px'
            }
          }}
        >
          <Table sx={{ width: '100%', tableLayout: 'fixed', display: 'table' }} aria-label="spreadsheet data">
            <TableHead sx={{ 
              bgcolor: 'black',
              '& .MuiTableRow-root': {
                height: 'auto'  // Changed from a fixed 32px to auto
              },
              '& .MuiTableCell-root': {
                fontWeight: '900 !important',
                fontSize: '1.25rem !important',
                fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif !important',
                padding: '4px 6px !important',
                height: '32px !important',
                lineHeight: '1.2 !important',
                color: 'white !important',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                '&.song-header': {
                  paddingLeft: '24px !important',
                  paddingRight: '6px !important'
                }
              }
            }}>
              <TableRow>
                {/* Position Column */}
                <TableCell 
                  sx={{
                    fontWeight: 900,
                    whiteSpace: 'nowrap',
                    padding: '8px 16px',
                    color: 'white',
                    bgcolor: 'black',
                    position: 'sticky',
                    left: 0,
                    width: '160px',
                    zIndex: 2,
                    borderRight: 'none',
                    textAlign: 'center',
                    fontSize: '1.25rem',
                    height: '48px',
                    lineHeight: '1.5',
                    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                    cursor: 'pointer', // Add cursor pointer to indicate it's clickable
                    userSelect: 'none', // Prevent text selection
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  className="sticky-column position"
                  onClick={toggleSortDirection} // Add click handler to toggle sort direction
                >
                  Position
                  {sortAscending ? ' ↑' : ' ↓'} {/* Show sort direction indicator */}
                </TableCell>

                {/* Reorder Column */}
                <TableCell 
                  sx={{
                    fontWeight: 900,
                    whiteSpace: 'nowrap',
                    padding: '8px 16px',
                    color: 'white',
                    bgcolor: 'black',
                    position: 'sticky',
                    left: '160px',
                    width: '60px',
                    zIndex: 2,
                    borderLeft: 'none',
                    borderRight: 'none',
                    textAlign: 'center',
                    fontSize: '1.25rem',
                    height: '48px',
                    lineHeight: '1.5',
                    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif'
                  }}
                  className="sticky-column reorder"
                >
                </TableCell>

                {/* Thumbnail Column */}
                <TableCell 
                  sx={{
                    fontWeight: 900,
                    whiteSpace: 'nowrap',
                    padding: '8px 16px',
                    color: 'white',
                    bgcolor: 'black',
                    position: 'sticky',
                    left: '220px',
                    width: '187px',
                    zIndex: 2,
                    borderLeft: 'none',
                    borderRight: 'none',
                    height: '48px',
                    lineHeight: '1.5',
                    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif'
                  }}
                  className="sticky-column thumbnail"
                >
                </TableCell>

                {/* Rest of the columns */}
                {data.headers // <-- Keep iterating over data.headers
                  // REMOVE the .filter() call here
                  .map((header, index) => { // <-- MAP DIRECTLY
                    // ---> REMOVE CONSOLE LOG HERE (if it exists) <--- 
                    // console.log("[TableHead] Processing header:", header);

                    // Ensure we skip any potentially remaining empty headers after loadData
                    if (header.trim() === '') return null; 

                    // Skip columns that should definitely not have a header cell rendered here
                    const lowerHeader = header.toLowerCase().trim();
                    if (
                      lowerHeader === 'position' ||
                      lowerHeader.includes('youtube link') ||
                      lowerHeader.includes('artwork url') ||
                      lowerHeader === 's3 url' ||
                      lowerHeader === 'voice over text' ||
                      lowerHeader === 'voice over url' ||
                      lowerHeader === 'vo in' || // Keep the correct names here just in case
                      lowerHeader === 'vo out' ||  // Keep the correct names here just in case
                      lowerHeader === 'in point' || // Hide seek in (in point) column
                      lowerHeader === 'seek in' || // Alternative name that might be used
                      lowerHeader === 'zoom' || // Also hide zoom column
                      lowerHeader === 'duration' || // Hide duration column
                      lowerHeader === 'vertical url' // Hide Vertical URL column
                    ) {
                      return null;
                    }

                    const shouldCenterAlign = header.toLowerCase().includes('last week') ||
                      header.toLowerCase().includes('this week') ||
                      header.toLowerCase().includes('peak') ||
                      header.toLowerCase() === 'weeks' || // <-- CHANGE HERE: === 'weeks'
                      header.toLowerCase().includes('duration') ||
                      header.toLowerCase().includes('in point');
                    
                    return (
                      <TableCell 
                        key={header + '-' + index} // Use header and index for a more robust key
                        align={shouldCenterAlign ? "center" : "left"}
                        sx={{
                          fontWeight: 900,
                          whiteSpace: 'nowrap',
                          padding: '8px 16px',
                          color: 'white',
                          bgcolor: 'black',
                          height: '48px',
                          lineHeight: '1.5',
                          fontSize: '1.25rem',
                          fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                          ...(header.toLowerCase().includes('last week') && {
                            width: '100px',
                            minWidth: '100px',
                            maxWidth: '100px'
                          }),
                          ...(header.toLowerCase().includes('peak') && {
                            width: '100px',
                            minWidth: '100px',
                            maxWidth: '100px'
                          }),
                          ...(header.toLowerCase() === 'weeks' && {
                            width: '100px',
                            minWidth: '100px',
                            maxWidth: '100px'
                          })
                        }}
                        className={header.toLowerCase().includes('song') ? 'song-header' : ''}
                      >
                        {header}
                      </TableCell>
                    );
                  })}
              </TableRow>
            </TableHead>
            <TableBody>
              {/* Generate sorted rows based on position value and current sort direction */}
              {(() => {
                // First, extract and filter valid rows
                const validRows = data.rows
                  .map((row, idx) => ({ row, idx }))
                  .filter(({ row }) => {
                    const posIndex = data.headers.findIndex(h => h.toLowerCase() === 'position');
                    if (posIndex === -1) return false;
                    const pos = parseInt(row[posIndex], 10);
                    return !isNaN(pos) && pos >= 1 && pos <= 10;
                  });

                // Sort rows based on position value and current sort direction
                const sortedRows = [...validRows].sort((a, b) => {
                  const posIndex = data.headers.findIndex(h => h.toLowerCase() === 'position');
                  const posA = parseInt(a.row[posIndex], 10);
                  const posB = parseInt(b.row[posIndex], 10);
                  
                  // If ascending (1-10), use posA - posB; if descending (10-1), use posB - posA
                  return sortAscending ? posA - posB : posB - posA;
                });

                // Return the sorted rows as JSX
                return sortedRows.map(({ row, idx: rowIndex }) => {
                // Find the Position column index IN THE ORIGINAL headers
                const originalPositionIndex = data.headers.findIndex(header => 
                  header.toLowerCase() === 'position'
                );
                
                  // const position = parseInt(row[originalPositionIndex], 10); // Removed unused

                // Find the Song and Artist column indices IN THE ORIGINAL headers
                const originalSongColumnIndex = data.headers.findIndex(header => 
                  header.toLowerCase().includes('song')
                );
                const originalArtistColumnIndex = data.headers.findIndex(header => 
                  header.toLowerCase().includes('artist')
                );
                
                const songName = originalSongColumnIndex !== -1 ? String(row[originalSongColumnIndex] || '').trim() : '';
                const artistName = originalArtistColumnIndex !== -1 ? String(row[originalArtistColumnIndex] || '').trim() : '';
                const videoKey = normalizeSongArtistKey(songName, artistName); // Use consistent normalization
                
                  // Add debug logging to the main table rendering section for thumbnails
                console.log(`[SpreadsheetViewer Render] Row ${rowIndex}: Song="${songName}", Artist="${artistName}", Generated Key="${videoKey}"`);
                const videoInfo = videoData[videoKey];
                  // Log the videoData keys to help debugging
                  console.log(`[SpreadsheetViewer Render] videoData contains ${Object.keys(videoData).length} keys.`);
                console.log(`[SpreadsheetViewer Render] Row ${rowIndex}: Lookup result for key "${videoKey}":`, videoInfo);

                const videoId = videoInfo?.youtubeLink ? getYouTubeVideoId(videoInfo.youtubeLink) : null;
                  console.log(`[SpreadsheetViewer Render] Row ${rowIndex}: Video ID: ${videoId}`);
                
                // --- Get indices of VISIBLE columns for rendering ---
                const visibleHeaders = data.headers.filter(header => {
                    const lowerHeader = header.toLowerCase().trim(); // Trim whitespace
                    return !(
                      lowerHeader === '' || // <-- Add check for empty string
                      lowerHeader === 'position' ||
                      lowerHeader.includes('youtube link') ||
                      lowerHeader.includes('artwork url') ||
                      lowerHeader === 's3 url' ||
                      lowerHeader === 'voice over text' ||
                      lowerHeader === 'voice over url' ||
                      lowerHeader === 'voice over in' ||
                        lowerHeader === 'voice over out' ||
                        lowerHeader === 'in point' || // Hide seek in (in point) column
                        lowerHeader === 'seek in' || // Alternative name that might be used
                        lowerHeader === 'vo in' || // Consistent with TableHead filter
                        lowerHeader === 'vo out' || // Consistent with TableHead filter
                        lowerHeader === 'zoom' || // Also hide zoom column
                        lowerHeader === 'duration' || // Hide duration column
                        lowerHeader === 'vertical url' // Hide Vertical URL column
                    );
                  });

                return (
                  <TableRow key={`${rowIndex}-main`}
                    sx={{
                      bgcolor: rowIndex % 2 === 0 ? 'background.paper' : 'rgba(0, 0, 0, 0.02)',
                      '& .sticky-column': {
                        bgcolor: 'inherit !important'
                      },
                      '& td': {
                        borderBottom: 'none'
                      }
                    }}
                  >
                    {/* Position Column (uses original index for data access) */}
                    <TableCell
                      onClick={() => {
                        // Remove navigation, make it non-interactive
                      }}
                      sx={{ 
                        position: 'sticky',
                        left: 0,
                        width: '160px',
                        height: '70px', // Adjusted height? Check consistency
                        zIndex: 1,
                        borderRight: 'none',
                        textAlign: 'center',
                        padding: '8px 0 0 0',
                        bgcolor: 'inherit',
                        cursor: 'default', // Change to default cursor
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'center'
                      }}
                      className="sticky-column position"
                    >
                      <Typography
                        sx={{
                          fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                          fontWeight: 900,
                          fontSize: {
                            xs: '4rem',
                            sm: '5rem',
                            md: '6rem'
                          },
                          lineHeight: 1,
                          width: '100%',
                          textAlign: 'center',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'black'
                        }}
                      >
                        {row[originalPositionIndex]} {/* Use original index */}
                      </Typography>
                    </TableCell>

                    {/* Reorder Column */}
                    <TableCell
                      sx={{ 
                        position: 'sticky',
                        left: '160px',
                        width: '60px',
                        height: '70px', // Check height consistency
                        zIndex: 1,
                        borderLeft: 'none',
                        borderRight: 'none',
                        textAlign: 'center',
                        padding: '8px 0 0 0',
                        bgcolor: 'inherit',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '4px'
                      }}
                      className="sticky-column reorder"
                    >
                      <IconButton
                        size="small"
                        onClick={() => handleMoveSong(rowIndex, 'up')}
                        disabled={rowIndex === 0}
                        sx={{ 
                          padding: '4px',
                          '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
                        }}
                      >
                        <ArrowUpwardIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleMoveSong(rowIndex, 'down')}
                        disabled={rowIndex === data.rows.length - 1} // Needs adjustment if rows are filtered
                        sx={{ 
                          padding: '4px',
                          '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
                        }}
                      >
                        <ArrowDownwardIcon fontSize="small" />
                      </IconButton>
                    </TableCell>

                    {/* Thumbnail / Add Video Column */}
                    <TableCell
                      className="sticky-column thumbnail"
                      sx={{
                        position: 'sticky',
                        left: '220px', 
                        width: '187px',
                        height: '106px',
                        zIndex: 1,
                        borderLeft: 'none',
                        borderRight: 'none',
                        padding: '0', 
                        bgcolor: 'inherit'
                      }}
                    >
                      {videoId ? (
                        <img
                          src={`https://img.youtube.com/vi/${videoId}/mqdefault.jpg`}
                          alt="Video thumbnail"
                          style={{
                            width: '187px',
                            height: '106px',
                            objectFit: 'cover',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            display: 'block' 
                          }}
                          onClick={() => {
                              if (songName && artistName) {
                                // Get the s3 url if available to pass along with the navigation
                                const s3UrlColIndex = data.headers.findIndex(h => h.toLowerCase() === 's3 url');
                                const s3Url = s3UrlColIndex !== -1 ? row[s3UrlColIndex] : null;
                                
                                // Navigate to Video Manager with song/artist params and player parameter
                                // Include the S3 URL if available
                                const queryParams = new URLSearchParams();
                                queryParams.append('song', songName);
                                queryParams.append('artist', artistName);
                                queryParams.append('player', 'true');
                                if (s3Url) {
                                  queryParams.append('s3url', s3Url);
                                }
                                navigate(`/video-manager?${queryParams.toString()}`);
                              } else if (videoInfo?.youtubeLink) {
                               window.open(videoInfo.youtubeLink, '_blank');
                             }
                          }}
                        />
                      ) : (
                        <Box sx={{ 
                            width: '100%', 
                            height: '100%', 
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                         }}>
                          <IconButton 
                             onClick={() => handleRequestVideoAdd(rowIndex)}
                             title="Add YouTube Video Link"
                             sx={{ 
                                 color: 'rgba(0, 0, 0, 0.54)'
                             }}
                          >
                            <AddCircleOutlineIcon sx={{ fontSize: 40 }} /> 
                          </IconButton>
                        </Box>
                      )}
                    </TableCell>

                    {/* Rest of the columns */}
                    {visibleHeaders.map((header, visibleIndex) => { // <-- MAP OVER FILTERED HEADERS
                      // Find the original index corresponding to this visible header
                      const originalIndex = data.headers.findIndex(h => h === header); 
                      if (originalIndex === -1) return null; // Should not happen if filtering is correct

                      const cellValue = row[originalIndex]; // Use original index to get cell value

                      const shouldCenterAlign = header.toLowerCase().includes('last week') ||
                        header.toLowerCase().includes('this week') ||
                        header.toLowerCase().includes('peak') ||
                        header.toLowerCase() === 'weeks' || // <-- CHANGE HERE: === 'weeks'
                        header.toLowerCase().includes('duration') ||
                        header.toLowerCase().includes('in point');
                      
                      return (
                        <TableCell
                          key={header + '-' + visibleIndex} // Use visible index for key
                          onClick={() => handleCellClick(rowIndex, originalIndex, cellValue)} // Pass ORIGINAL index to handler
                          align={shouldCenterAlign ? "center" : "left"}
                          sx={{ 
                            cursor: isSignedIn ? 'pointer' : 'default',
                            whiteSpace: 'nowrap',
                            padding: '16px 24px 0 24px',
                            height: '106px',
                            bgcolor: 'inherit',
                            display: 'table-cell',
                            verticalAlign: 'top',
                            ...(header.toLowerCase().includes('song') && {
                              paddingLeft: '24px !important'
                            }),
                            ...(header.toLowerCase().includes('last week') && {
                              width: '100px',
                              minWidth: '100px',
                              maxWidth: '100px'
                            }),
                            ...(header.toLowerCase().includes('peak') && {
                              width: '100px',
                              minWidth: '100px',
                              maxWidth: '100px'
                            }),
                            ...(header.toLowerCase() === 'weeks' && {
                              width: '100px',
                              minWidth: '100px',
                              maxWidth: '100px'
                            })
                          }}
                        >
                          {/* Pass ORIGINAL index to renderCell */}
                          {renderCell(cellValue, header, rowIndex, originalIndex)} 
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
                });
              })()}
            </TableBody>
          </Table>
        </TableContainer>
      ) : null}
    </Box>
  );
};

export default SpreadsheetViewer;